import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasFeature } from "../lib/features";
import { LockedFeature } from "../components/FeatureGuard";
import SeatUsageNudge from "../components/SeatUsageNudge";
import TenantAlertSettings from "../components/TenantAlertSettings";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import SlaAlertSettingsCard from "../components/SlaAlertSettingsCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import QuoteSlaSettingsSection from "../components/QuoteSlaSettingsSection";
import SubJobsSettingsSection from "../components/SubJobsSettingsSection";
import PhotoQaSettings from "../components/PhotoQaSettings";
import BookingSettings from "../components/BookingSettings";
import InboxSettings from "../components/inbox/InboxSettings";
import ResidentTriageSettings from "../components/ResidentTriageSettings";
import UserPermissionsSection from "../components/UserPermissionsSection";
import SaasPlatformSection from "../components/SaasPlatformSection";
import { Plus, PencilSimple, Trash, ArrowsClockwise, X, Bell, Link, LinkSimple, Copy, Prohibit, Key, PaperPlaneTilt, EnvelopeSimple, CurrencyGbp, TrendUp, Lightning, Warning, Palette, Receipt, ShieldCheck, Translate, Gauge, Users, FlowArrow, FileText, ClockCounterClockwise, Repeat, Wrench, Bank, UserGear, Buildings, CreditCard, Plugs, TreeStructure, Camera, Tray, CalendarCheck, HouseLine, SidebarSimple } from "@phosphor-icons/react";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

import { usePushNotifications } from "../lib/usePush";
import { useI18n } from "../contexts/I18nContext";
import AcknowledgmentEmailSettings from "./AcknowledgmentEmailSettings";
import EmailSendingSettings from "../components/EmailSendingSettings";
import StripeKeysCard from "../components/StripeKeysCard";
import StripeConnectCard from "../components/StripeConnectCard";
import IntegrationsHub from "../components/integrations/IntegrationsHub";
import SidebarMenuSettings from "../components/SidebarMenuSettings";
import { fmtDate } from "../lib/dateUtil";

const CONFIGURABLE_ROLES = ["project_manager", "team_member"];
const NOTIF_KEYS = [
  { key: "task_assigned", label: "Task Assigned" },
  { key: "task_completed", label: "Task Completed" },
  { key: "task_approved", label: "Task Approved" },
  { key: "task_rejected", label: "Task Rejected" },
  { key: "work_order_issued", label: "Works Order Issued" },
  { key: "compliance_reminder", label: "Compliance Reminder" },
  { key: "email_enabled", label: "Email Notifications" },
];

const SETTINGS_GROUPS = [
  {
    label: "Team",
    items: [
      { value: "users", label: "Users", icon: Users, testid: "settings-tab-users" },
      { value: "permissions", label: "User Permissions", icon: UserGear, testid: "settings-tab-permissions" },
      { value: "mfa", label: "MFA Security", icon: ShieldCheck, testid: "settings-tab-mfa" },
      { value: "language", label: "Language", icon: Translate, testid: "settings-tab-language" },
      { value: "sidebar", label: "Sidebar Menu", icon: SidebarSimple, testid: "settings-tab-sidebar", roles: ["super_admin", "admin"] },
    ],
  },
  {
    label: "Operations",
    items: [
      { value: "workflows", label: "Workflows", icon: FlowArrow, testid: "settings-tab-workflows" },
      { value: "templates", label: "Templates", icon: FileText, testid: "settings-tab-templates" },
      { value: "escalation", label: "Emergency Escalation", icon: Lightning, testid: "settings-tab-escalation" },
      { value: "sla-watch", label: "Supplier SLA", icon: Gauge, testid: "settings-tab-sla-watch" },
      { value: "quote-sla", label: "Quote SLAs", icon: Gauge, testid: "settings-tab-quote-sla" },
      { value: "sub-jobs", label: "Sub-jobs", icon: TreeStructure, testid: "settings-tab-sub-jobs", roles: ["super_admin", "admin"] },
      { value: "photo-qa", label: "AI Photo QA", icon: Camera, testid: "settings-tab-photo-qa", roles: ["super_admin", "admin"] },
      { value: "email-inbox", label: "Email Inbox", icon: Tray, testid: "settings-tab-email-inbox", roles: ["super_admin", "admin"] },
    ],
  },
  {
    label: "Client-facing",
    items: [
      { value: "portal", label: "Client Portal", icon: Link, testid: "settings-tab-portal" },
      { value: "booking", label: "Self-booking", icon: CalendarCheck, testid: "settings-tab-booking", roles: ["super_admin", "admin"] },
      { value: "resident-triage", label: "Resident Triage", icon: HouseLine, testid: "settings-tab-resident-triage", roles: ["super_admin", "admin"] },
      { value: "email-sending", label: "Email Sending", icon: EnvelopeSimple, testid: "settings-tab-email-sending", roles: ["super_admin", "admin"] },
      { value: "card-payments", label: "Card Payments", icon: CreditCard, testid: "settings-tab-card-payments", roles: ["super_admin", "admin"] },
      { value: "quote-email", label: "Quote Email", icon: PaperPlaneTilt, testid: "settings-tab-quote-email" },
      { value: "ack-email", label: "Acknowledgment Email", icon: EnvelopeSimple, testid: "settings-tab-ack-email" },
      { value: "quote-followups", label: "Auto Follow-ups", icon: Repeat, testid: "settings-tab-quote-followups" },
      { value: "vat", label: "VAT", icon: CurrencyGbp, testid: "settings-tab-vat" },
      { value: "bank-details", label: "Bank Details", icon: Bank, testid: "settings-tab-bank-details" },
      { value: "markup", label: "Markup", icon: CurrencyGbp, testid: "settings-tab-markup" },
      { value: "pricing-calc", label: "Pricing Calculator", icon: ArrowsClockwise, testid: "settings-tab-pricing-calc" },
      { value: "price-books", label: "Price Books", icon: CurrencyGbp, testid: "settings-tab-price-books",
        onClick: (nav) => nav("/settings/price-books") },
    ],
  },
  {
    label: "Notifications",
    items: [
      { value: "notifications", label: "Notification Defaults", icon: Bell, testid: "settings-tab-notifications" },
      { value: "digest", label: "Weekly Digest", icon: EnvelopeSimple, testid: "settings-tab-digest" },
      { value: "cashflow-snapshot", label: "Monthly Cashflow", icon: TrendUp, testid: "settings-tab-cashflow-snapshot" },
    ],
  },
  {
    label: "Platform",
    items: [
      { value: "saas-platform", label: "SaaS Platform", icon: Buildings, testid: "settings-tab-saas-platform", roles: ["super_admin", "admin"] },
      { value: "integrations", label: "Integrations", icon: Plugs, testid: "settings-tab-integrations" },
      { value: "apikeys", label: "API Keys", icon: Key, testid: "settings-tab-apikeys" },
      { value: "audit", label: "Audit Logs", icon: ClockCounterClockwise, testid: "settings-tab-audit" },
      { value: "branding", label: "Branding", icon: Palette, testid: "settings-tab-branding",
        onClick: (nav) => nav("/platform/branding") },
      { value: "billing", label: "Billing", icon: Receipt, testid: "settings-tab-billing",
        onClick: (nav) => nav("/platform/billing") },
      { value: "tenant-repair", label: "Data Repair", icon: Wrench, testid: "settings-tab-tenant-repair",
        onClick: (nav) => nav("/settings/tenant-repair") },
    ],
  },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { language, setLanguage, LANGUAGES } = useI18n();
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userForm, setUserForm] = useState({ email: "", password: "", name: "", role: "team_member", phone: "", company: "" });
  // Phase 241 — Users sub-tabs (admin / in_house / supplier) + helper to set role from subtab
  const [userSubTab, setUserSubTab] = useState("admin");
  const setNewUserRole = (role) => setUserForm(f => ({ ...f, role }));

  // Phase 321 — settings tab visibility per custom role
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(() => searchParams.get("tab") || "users");
  const setActiveTab = useCallback((value) => {
    setActiveTabState(value);
    setSearchParams(value === "users" ? {} : { tab: value }, { replace: true });
  }, [setSearchParams]);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTabState(t);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sectionPerms, setSectionPerms] = useState(null);
  const [customRoles, setCustomRoles] = useState([]);
  useEffect(() => {
    API.get("/permissions/me").then(({ data }) => setSectionPerms(data)).catch(() => {});
    API.get("/permissions/roles").then(({ data }) => setCustomRoles(data.roles || [])).catch(() => {});
  }, []);
  const tabAllowed = useCallback(
    (value) => !sectionPerms || sectionPerms.full_access || sectionPerms.permissions?.[`settings.${value}`] !== false,
    [sectionPerms]
  );
  useEffect(() => {
    if (sectionPerms && !sectionPerms.full_access && !tabAllowed(activeTab)) {
      for (const g of SETTINGS_GROUPS) {
        const t = g.items.find(i => tabAllowed(i.value));
        if (t) { setActiveTab(t.value); return; }
      }
    }
  }, [sectionPerms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Workflows state
  const [statusConfig, setStatusConfig] = useState(null);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [newProjectStatus, setNewProjectStatus] = useState("");
  const [newTaskStatus, setNewTaskStatus] = useState("");
  const [savingWorkflows, setSavingWorkflows] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Notification defaults state
  const [notifDefaults, setNotifDefaults] = useState(null);
  const [loadingNotifDefaults, setLoadingNotifDefaults] = useState(true);
  const [savingNotifDefaults, setSavingNotifDefaults] = useState(false);
  const [applyingRole, setApplyingRole] = useState(null);
  const push = usePushNotifications();

  // Form nudge settings
  const [formNudgeHours, setFormNudgeHours] = useState(24);
  const [formNudgeEscalateAfter, setFormNudgeEscalateAfter] = useState(3);
  const [savingFormNudge, setSavingFormNudge] = useState(false);

  useEffect(() => {
    API.get("/tenant/notification-settings")
      .then(({ data }) => {
        setFormNudgeHours(data.form_nudge_hours ?? 24);
        setFormNudgeEscalateAfter(data.form_nudge_escalate_after ?? 3);
      })
      .catch(() => {});
  }, []);

  const saveFormNudge = async () => {
    setSavingFormNudge(true);
    try {
      const hrs = Math.max(0, Math.min(720, parseInt(formNudgeHours) || 0));
      const esc = Math.max(1, Math.min(20, parseInt(formNudgeEscalateAfter) || 3));
      await API.put("/tenant/notification-settings", { form_nudge_hours: hrs, form_nudge_escalate_after: esc });
      setFormNudgeHours(hrs);
      setFormNudgeEscalateAfter(esc);
      toast.success(hrs === 0 ? "Form reminder disabled" : `Reminder set to ${hrs}h, escalates after ${esc}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSavingFormNudge(false);
    }
  };

  // Client portal state
  const [portalLinks, setPortalLinks] = useState([]);
  const [showCreateLink, setShowCreateLink] = useState(false);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [linkForm, setLinkForm] = useState({ project_id: "", client_name: "", client_email: "", expires_days: "30" });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const { data } = await API.get("/audit-logs", { params: { limit: 100 } });
      setAuditLogs(data.logs);
      setAuditTotal(data.total);
    } catch (err) { console.error(err); }
  }, []);

  const fetchStatusConfig = useCallback(async () => {
    try {
      const { data } = await API.get("/settings/statuses");
      setStatusConfig(data);
      setProjectStatuses(data.project_statuses || []);
      setTaskStatuses(data.task_statuses || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const { data } = await API.get("/project-templates");
      setTemplates(data);
    } catch (err) { console.error(err); }
    finally { setLoadingTemplates(false); }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchAuditLogs();
    fetchStatusConfig();
    fetchTemplates();
    API.get("/notification-preferences/defaults").then(({ data }) => setNotifDefaults(data)).catch(() => {}).finally(() => setLoadingNotifDefaults(false));
    API.get("/client-portal/links").then(({ data }) => setPortalLinks(data)).catch(() => {});
    API.get("/projects?limit=200").then(({ data }) => setProjects(data.projects || [])).catch(() => {});
    API.get("/suppliers?limit=200").then(({ data }) => setVendors(Array.isArray(data) ? data : data.vendors || [])).catch(() => {});
    fetchApiKeys();
  }, [fetchUsers, fetchAuditLogs, fetchStatusConfig, fetchTemplates]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await API.post("/users/invite", { name: userForm.name, email: userForm.email, role: userForm.role });
      toast.success("Invitation sent successfully");
      setShowCreateUser(false);
      setUserForm({ email: "", password: "", name: "", role: "team_member", phone: "", company: "" });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send invitation"); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const isCustom = (editUser.role || "").startsWith("custom:");
      if (isCustom) {
        const roleId = editUser.role.slice(7);
        if (roleId !== editUser._origCustomRoleId) await API.put("/permissions/assign", { user_id: editUser._id, role_id: roleId });
      } else if (editUser._origCustomRoleId) {
        await API.put("/permissions/assign", { user_id: editUser._id, role_id: null });
      }
      const update = { name: editUser.name, phone: editUser.phone, company: editUser.company, active: editUser.active, operations_director: !!editUser.operations_director };
      if (!isCustom) update.role = editUser.role;
      if (editUser.newPassword) update.password = editUser.newPassword;
      await API.put(`/users/${editUser._id}`, update);
      toast.success("User updated");
      setShowEditUser(false);
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to update user"); }
  };

  const handleDeactivate = async (userId) => {
    try {
      await API.delete(`/users/${userId}`);
      toast.success("User deactivated");
      fetchUsers();
    } catch { toast.error("Failed"); }
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handlePermanentDelete = async (userId) => {
    try {
      await API.delete(`/users/${userId}/permanent`);
      toast.success("User permanently deleted");
      setShowDeleteConfirm(null);
      setDeleteConfirmText("");
      fetchUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to delete user"); }
  };

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ app_name: "", description: "" });
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [showKeyFiles, setShowKeyFiles] = useState(null);
  const [keyFiles, setKeyFiles] = useState([]);

  const fetchApiKeys = async () => {
    try { const { data } = await API.get("/api-keys"); setApiKeys(data); } catch { }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post("/api-keys", newKeyForm);
      setNewKeyResult(data);
      setNewKeyForm({ app_name: "", description: "" });
      fetchApiKeys();
    } catch { toast.error("Failed to create API key"); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await API.delete(`/api-keys/${keyId}`);
      toast.success("API key revoked");
      fetchApiKeys();
    } catch { toast.error("Failed to revoke key"); }
  };

  const handleDeleteKey = async (keyId) => {
    try {
      await API.delete(`/api-keys/${keyId}/permanent`);
      toast.success("API key and files deleted");
      fetchApiKeys();
    } catch { toast.error("Failed to delete key"); }
  };

  const viewKeyFiles = async (keyId) => {
    try {
      const { data } = await API.get(`/api-keys/${keyId}/files`);
      setKeyFiles(data);
      setShowKeyFiles(keyId);
    } catch { toast.error("Failed to load files"); }
  };

  const handleSaveWorkflows = async () => {
    setSavingWorkflows(true);
    try {
      await API.put("/settings/statuses", {
        project_statuses: projectStatuses,
        task_statuses: taskStatuses
      });
      toast.success("Status workflows updated");
    } catch { toast.error("Failed to save workflows"); }
    finally { setSavingWorkflows(false); }
  };

  const addProjectStatus = () => {
    const val = newProjectStatus.trim().toLowerCase().replace(/\s+/g, "_");
    if (val && !projectStatuses.includes(val)) {
      setProjectStatuses([...projectStatuses, val]);
      setNewProjectStatus("");
    }
  };

  const addTaskStatus = () => {
    const val = newTaskStatus.trim().toLowerCase().replace(/\s+/g, "_");
    if (val && !taskStatuses.includes(val)) {
      setTaskStatuses([...taskStatuses, val]);
      setNewTaskStatus("");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await API.delete(`/project-templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch { toast.error("Failed to delete template"); }
  };

  const handleToggleRoleDefault = (role, key) => {
    setNotifDefaults(prev => ({
      ...prev,
      role_overrides: {
        ...prev.role_overrides,
        [role]: { ...prev.role_overrides?.[role], [key]: !(prev.role_overrides?.[role]?.[key] ?? true) }
      }
    }));
  };

  const handleSaveNotifDefaults = async () => {
    setSavingNotifDefaults(true);
    try {
      await API.put("/notification-preferences/defaults", notifDefaults);
      toast.success("Notification defaults saved");
    } catch { toast.error("Failed to save defaults"); }
    finally { setSavingNotifDefaults(false); }
  };

  const handleApplyDefaults = async (role) => {
    setApplyingRole(role);
    try {
      const { data } = await API.post("/notification-preferences/apply-defaults", { role });
      toast.success(data.message);
    } catch { toast.error("Failed to apply defaults"); }
    finally { setApplyingRole(null); }
  };

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight mb-6">Settings</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-60 shrink-0">
          <TabsList
            data-testid="settings-tabs"
            className="flex flex-row flex-wrap lg:flex-col h-auto w-full p-1.5 gap-1 bg-muted/40 dark:bg-muted/30 rounded-lg border border-border items-stretch lg:items-start justify-start"
          >
            {SETTINGS_GROUPS.map((group, gi) => {
              const allowedItems = group.items.filter(t => tabAllowed(t.value) && (!t.roles || t.roles.includes(user?.role)));
              if (!allowedItems.length) return null;
              return (
              <div key={group.label} className={`w-full ${gi > 0 ? "lg:mt-3" : ""}`}>
                <p className="hidden lg:block px-2.5 pt-1 pb-1.5 text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{group.label}</p>
                {allowedItems.map(t => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    onClick={t.onClick ? () => t.onClick(navigate) : undefined}
                    data-testid={t.testid}
                    className="w-full justify-start lg:h-9 h-8 px-3 gap-2 text-xs lg:text-[13px] font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary"
                  >
                    <t.icon size={15} weight="duotone" className="shrink-0 opacity-80" />
                    <span className="truncate">{t.label}</span>
                  </TabsTrigger>
                ))}
              </div>
              );
            })}
          </TabsList>
        </aside>

        <section className="flex-1 min-w-0">

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          {(() => {
            const ADMIN_ROLES = ["super_admin", "admin", "project_manager", "hs_manager", "hr_manager"];
            const IN_HOUSE_ROLES = ["team_member", "in_house"];
            const adminUsers = users.filter(u => ADMIN_ROLES.includes(u.role));
            const inHouseUsers = users.filter(u => IN_HOUSE_ROLES.includes(u.role));
            const supplierUsers = users.filter(u => ["vendor", "supplier"].includes(u.role));
            const sub = userSubTab || "admin";
            const visible = sub === "admin" ? adminUsers : sub === "in_house" ? inHouseUsers : supplierUsers;
            const inviteLabelFor = (s) => s === "in_house" ? "Invite In-House Operative" : "Invite Admin / PM";
            return (
              <>
                <SeatUsageNudge className="mb-3" />
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5" role="tablist">
                    <button onClick={() => setUserSubTab("admin")} className={`px-3 py-1.5 text-xs font-medium rounded ${sub === "admin" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="user-subtab-admin">Admins ({adminUsers.length})</button>
                    <button onClick={() => setUserSubTab("in_house")} className={`px-3 py-1.5 text-xs font-medium rounded ${sub === "in_house" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="user-subtab-in-house">In-house ({inHouseUsers.length})</button>
                    <button onClick={() => setUserSubTab("supplier")} className={`px-3 py-1.5 text-xs font-medium rounded ${sub === "supplier" ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="user-subtab-supplier">Suppliers ({supplierUsers.length})</button>
                  </div>
                  {sub !== "supplier" && (
                    <Button size="sm" onClick={() => { setNewUserRole(sub === "in_house" ? "team_member" : "admin"); setShowCreateUser(true); }} data-testid="create-user-btn">
                      <PaperPlaneTilt size={14} className="mr-1" /> {inviteLabelFor(sub)}
                    </Button>
                  )}
                  {sub === "supplier" && (
                    <Button size="sm" variant="outline" onClick={() => navigate("/suppliers")} data-testid="manage-suppliers-btn">
                      Manage Suppliers →
                    </Button>
                  )}
                </div>

                {sub === "supplier" && (
                  <div className="text-[11px] bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-3 py-2 rounded-md mb-3">
                    🔒 Suppliers (read-only here): supplier accounts are managed from the dedicated Suppliers page. They cannot be edited as platform users.
                  </div>
                )}

                {/* Phase 253 — Force-delete orphan-by-email tool */}
                {sub !== "supplier" && <OrphanWipeToolPanel onWiped={fetchUsers} />}

                {loading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
                ) : visible.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-border rounded-md text-sm text-muted-foreground">
                    {sub === "admin" ? "No admin users yet." : sub === "in_house" ? "No in-house operatives yet — invite one above." : "No supplier user accounts."}
                  </div>
                ) : (
                  <div className="border border-border rounded-md overflow-hidden">
                    <table className="w-full text-sm" data-testid="users-table">
                      <thead>
                        <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="text-left px-4 py-3 font-semibold">User</th>
                          <th className="text-left px-4 py-3 font-semibold">Role</th>
                          <th className="text-left px-4 py-3 font-semibold">Status</th>
                          <th className="text-left px-4 py-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {visible.map(u => (
                          <tr key={u._id} className="row-hover" data-testid={`user-row-${u._id}`}>
                            <td className="px-4 py-3">
                              <p className="font-medium">{u.name}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </td>
                      <td className="px-4 py-3">
                        <RoleReassignSelect user={u} canEdit={u._id !== user._id} onChanged={fetchUsers} customRoles={customRoles} />
                      </td>
                      <td className="px-4 py-3">
                        {u.invite_status === "pending" ? (
                          <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-700">
                            <EnvelopeSimple size={10} className="mr-0.5" weight="fill" /> Invited
                          </Badge>
                        ) : (
                          <Badge variant={u.active ? "default" : "secondary"} className="text-[10px]">{u.active ? "Active" : "Inactive"}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditUser({...u, newPassword: "", _baseRole: u.role, _origCustomRoleId: u.custom_role_id || "", role: (u.custom_role_id && ADMIN_ROLES.includes(u.role)) ? `custom:${u.custom_role_id}` : u.role}); setShowEditUser(true); }} data-testid={`edit-user-${u._id}`}>
                            <PencilSimple size={14} />
                          </Button>
                          {["super_admin", "admin", "project_manager"].includes(u.role) && (
                            <Button variant="ghost" size="icon"
                              className={`h-7 w-7 ${u.operations_director ? "text-indigo-600 hover:text-indigo-700" : "text-muted-foreground/40 hover:text-foreground"}`}
                              title={u.operations_director ? "Operations Director (can access Staff Monitoring) — click to revoke" : "Make Operations Director (grants Staff Monitoring access)"}
                              onClick={async () => {
                                try {
                                  await API.put(`/users/${u._id}`, { operations_director: !u.operations_director });
                                  toast.success(u.operations_director ? `${u.name} is no longer an Operations Director` : `${u.name} is now an Operations Director`);
                                  fetchUsers();
                                } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update"); }
                              }}
                              data-testid={`ops-director-toggle-${u._id}`}>
                              <UserGear size={14} weight={u.operations_director ? "fill" : "regular"} />
                            </Button>
                          )}
                          {u.invite_status === "pending" && u._id !== user._id && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-700"
                              onClick={async () => {
                                try {
                                  const { data } = await API.post(`/users/${u._id}/setup-link`);
                                  await navigator.clipboard.writeText(data.url);
                                  toast.success(`Setup link copied for ${data.email}`);
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Failed to generate setup link");
                                }
                              }}
                              title="Copy passwordless setup link"
                              data-testid={`copy-setup-link-${u._id}`}>
                              <LinkSimple size={14} />
                            </Button>
                          )}
                          {u.active && u._id !== user._id && (
                            <Button variant="ghost" size="sm" className="h-7 text-destructive hover:text-destructive text-xs" onClick={() => handleDeactivate(u._id)} data-testid={`deactivate-user-${u._id}`}>
                              Deactivate
                            </Button>
                          )}
                          {u._id !== user._id && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setShowDeleteConfirm(u)} title="Delete permanently" data-testid={`delete-user-${u._id}`}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
                )}

                {/* Phase 256 — User audit log sidebar */}
                <UserAuditLogPanel />
              </>
            );
          })()}

          {/* Permanent Delete Confirmation Dialog */}
          <Dialog open={!!showDeleteConfirm} onOpenChange={(open) => { if (!open) { setShowDeleteConfirm(null); setDeleteConfirmText(""); } }}>
            <DialogContent className="max-w-md" data-testid="delete-user-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo] text-destructive">Permanently Delete User</DialogTitle>
                <DialogDescription>
                  This action cannot be undone. This will permanently remove <span className="font-semibold text-foreground">{showDeleteConfirm?.name}</span> ({showDeleteConfirm?.email}) and all their associated data from the system.
                  {showDeleteConfirm?.role === "vendor" && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400 font-medium">This supplier's compliance documents will be deleted and their assigned tasks will be unassigned.</span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="border border-border rounded-md p-3 bg-muted/30 text-xs space-y-1">
                  <p><span className="font-semibold">Name:</span> {showDeleteConfirm?.name}</p>
                  <p><span className="font-semibold">Email:</span> {showDeleteConfirm?.email}</p>
                  <p><span className="font-semibold">Role:</span> {showDeleteConfirm?.role?.replace(/_/g, " ")}</p>
                </div>
                <div>
                  <Label>Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm</Label>
                  <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="Type DELETE to confirm" data-testid="delete-confirm-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowDeleteConfirm(null); setDeleteConfirmText(""); }}>Cancel</Button>
                <Button variant="destructive" disabled={deleteConfirmText !== "DELETE"} onClick={() => handlePermanentDelete(showDeleteConfirm._id)} data-testid="confirm-delete-user-btn">
                  Delete Permanently
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="mt-4" data-testid="workflows-tab-content">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold font-[Chivo]">Custom Status Workflows</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Configure available statuses for projects and tasks across the platform.</p>
              </div>
              <Button size="sm" onClick={handleSaveWorkflows} disabled={savingWorkflows} data-testid="save-workflows-btn">
                <ArrowsClockwise size={14} className={`mr-1 ${savingWorkflows ? "animate-spin" : ""}`} />
                {savingWorkflows ? "Saving..." : "Save Workflows"}
              </Button>
            </div>

            {/* Project Statuses */}
            <div className="border border-border rounded-md p-4">
              <h3 className="text-sm font-semibold mb-3">Project Statuses</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {projectStatuses.map(s => (
                  <div key={s} className="flex items-center gap-1 px-2.5 py-1 bg-muted rounded-md group" data-testid={`project-status-chip-${s}`}>
                    <span className="text-xs capitalize">{s.replace(/_/g, " ")}</span>
                    <button onClick={() => setProjectStatuses(projectStatuses.filter(x => x !== s))} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-project-status-${s}`}>
                      <X size={12} className="text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add new project status..."
                  value={newProjectStatus}
                  onChange={e => setNewProjectStatus(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addProjectStatus())}
                  className="flex-1 h-8 text-sm"
                  data-testid="new-project-status-input"
                />
                <Button size="sm" variant="outline" onClick={addProjectStatus} data-testid="add-project-status-btn">
                  <Plus size={14} />
                </Button>
              </div>
            </div>

            {/* Task Statuses */}
            <div className="border border-border rounded-md p-4">
              <h3 className="text-sm font-semibold mb-3">Task Statuses</h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {taskStatuses.map(s => (
                  <div key={s} className="flex items-center gap-1 px-2.5 py-1 bg-muted rounded-md group" data-testid={`task-status-chip-${s}`}>
                    <span className="text-xs capitalize">{s.replace(/_/g, " ")}</span>
                    <button onClick={() => setTaskStatuses(taskStatuses.filter(x => x !== s))} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-task-status-${s}`}>
                      <X size={12} className="text-muted-foreground hover:text-destructive" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add new task status..."
                  value={newTaskStatus}
                  onChange={e => setNewTaskStatus(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTaskStatus())}
                  className="flex-1 h-8 text-sm"
                  data-testid="new-task-status-input"
                />
                <Button size="sm" variant="outline" onClick={addTaskStatus} data-testid="add-task-status-btn">
                  <Plus size={14} />
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4" data-testid="templates-tab-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold font-[Chivo]">Project Templates</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Reusable project configurations with pre-defined tasks.</p>
            </div>
            <p className="text-sm text-muted-foreground">{templates.length} templates</p>
          </div>

          {loadingTemplates ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <p className="text-muted-foreground mb-1">No templates yet</p>
              <p className="text-xs text-muted-foreground">Save a project as a template from the project detail page.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {templates.map(t => (
                <div key={t.id} className="border border-border rounded-md p-4 hover:border-primary/30 transition-colors" data-testid={`template-card-${t.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">{t.name}</h3>
                      <p className="text-[10px] text-muted-foreground capitalize">{t.project_type?.replace(/_/g, " ")} | {t.priority}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteTemplate(t.id)} data-testid={`delete-template-${t.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{t.description}</p>}
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px]">{t.task_templates?.length || 0} tasks</Badge>
                    <span className="text-[10px] text-muted-foreground">by {t.created_by_name}</span>
                  </div>
                  {t.task_templates?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {t.task_templates.slice(0, 4).map((tt, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 bg-muted rounded">{tt.title}</span>
                      ))}
                      {t.task_templates.length > 4 && <span className="text-[9px] text-muted-foreground self-center">+{t.task_templates.length - 4}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Notification Defaults Tab */}
        <TabsContent value="notifications" className="mt-4" data-testid="notification-defaults-tab">
          {/* Push Notifications */}
          <div className="border border-border rounded-md p-4 mb-6" data-testid="push-notifications-card">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold font-[Chivo]">Browser Push Notifications</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {!push.supported ? "Push notifications are not supported in this browser." :
                   push.permission === 'denied' ? "Notifications blocked. Enable them in browser settings." :
                   push.subscribed ? "You will receive real-time browser notifications." :
                   "Enable to receive instant alerts for task updates, approvals, and compliance reminders."}
                </p>
              </div>
              {push.supported && push.permission !== 'denied' && (
                <Button
                  size="sm"
                  variant={push.subscribed ? "outline" : "default"}
                  disabled={push.loading}
                  onClick={async () => {
                    if (push.subscribed) {
                      await push.unsubscribe();
                      toast.success("Push notifications disabled");
                    } else {
                      const ok = await push.subscribe();
                      if (ok) toast.success("Push notifications enabled");
                      else toast.error("Could not enable push notifications");
                    }
                  }}
                  data-testid="push-toggle-btn"
                >
                  {push.loading ? "..." : push.subscribed ? "Disable Push" : "Enable Push"}
                </Button>
              )}
            </div>
          </div>

          {/* Form Outstanding Reminder */}
          <div className="border border-border rounded-md p-4 mb-6" data-testid="form-nudge-card">
            <div className="mb-3">
              <h3 className="text-sm font-semibold font-[Chivo]">Outstanding Form Reminder</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Email + push reminder to the assigned operative/supplier if required job forms are still unfilled X hours after they arrived on site. Once N reminders have been sent without action, the admin team is CC&apos;d too.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Hours after arrival</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={0}
                    max={720}
                    step={1}
                    className="w-24 h-8"
                    value={formNudgeHours}
                    onChange={(e) => setFormNudgeHours(e.target.value)}
                    data-testid="form-nudge-hours-input"
                  />
                  <span className="text-xs text-muted-foreground">hours (0 = off)</span>
                </div>
              </div>
              <div>
                <Label className="text-xs">Escalate to admins after</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    step={1}
                    className="w-24 h-8"
                    value={formNudgeEscalateAfter}
                    onChange={(e) => setFormNudgeEscalateAfter(e.target.value)}
                    data-testid="form-nudge-escalate-after-input"
                  />
                  <span className="text-xs text-muted-foreground">reminders</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={saveFormNudge} disabled={savingFormNudge} data-testid="form-nudge-save-btn">
                {savingFormNudge ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>

          <TenantAlertSettings />

          {loadingNotifDefaults ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
          ) : notifDefaults ? (
            <div className="space-y-6 max-w-4xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold font-[Chivo]">Role-Based Notification Defaults</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Configure default notification settings per role. New users inherit these defaults.</p>
                </div>
                <Button size="sm" onClick={handleSaveNotifDefaults} disabled={savingNotifDefaults} data-testid="save-notif-defaults-btn">
                  {savingNotifDefaults ? "Saving..." : "Save Defaults"}
                </Button>
              </div>

              <div className="border border-border rounded-md overflow-x-auto">
                <table className="w-full text-sm" data-testid="notif-defaults-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left px-4 py-2.5 font-semibold sticky left-0 bg-muted/50">Notification</th>
                      {CONFIGURABLE_ROLES.map(r => (
                        <th key={r} className="text-center px-4 py-2.5 font-semibold whitespace-nowrap capitalize">{r.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {NOTIF_KEYS.map(nk => (
                      <tr key={nk.key} data-testid={`notif-default-row-${nk.key}`}>
                        <td className="px-4 py-2.5 font-medium text-xs sticky left-0 bg-card">{nk.label}</td>
                        {CONFIGURABLE_ROLES.map(role => (
                          <td key={role} className="text-center px-4 py-2.5">
                            <Switch
                              checked={notifDefaults.role_overrides?.[role]?.[nk.key] ?? true}
                              onCheckedChange={() => handleToggleRoleDefault(role, nk.key)}
                              data-testid={`notif-toggle-${role}-${nk.key}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-border rounded-md p-4">
                <h3 className="text-sm font-semibold font-[Chivo] mb-3">Apply Defaults to Existing Users</h3>
                <p className="text-xs text-muted-foreground mb-3">Push current defaults to users who haven't customized their preferences yet.</p>
                <div className="flex flex-wrap gap-2">
                  {CONFIGURABLE_ROLES.map(role => (
                    <Button key={role} variant="outline" size="sm" onClick={() => handleApplyDefaults(role)} disabled={applyingRole === role} data-testid={`apply-defaults-${role}`}>
                      {applyingRole === role ? <ArrowsClockwise size={14} className="mr-1 animate-spin" /> : null}
                      <span className="capitalize">{role.replace(/_/g, " ")}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground">Unable to load notification defaults.</p>}
        </TabsContent>

        {/* Audit Tab */}
        <TabsContent value="audit" className="mt-4">
          <p className="text-sm text-muted-foreground mb-4">{auditTotal} total log entries</p>
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Entity</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Details</th>
                  <th className="text-left px-4 py-3 font-semibold">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {auditLogs.map(log => (
                  <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5">
                      <span className="text-xs font-medium capitalize">{log.action?.replace(/_/g, " ")}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-xs">{log.user_name}</p>
                      <p className="text-[10px] text-muted-foreground capitalize">{log.user_role?.replace(/_/g, " ")}</p>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <Badge variant="outline" className="text-[10px] capitalize">{log.entity_type}</Badge>
                    </td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground truncate max-w-xs">{log.details}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Client Portal Tab */}
        <TabsContent value="portal" className="mt-4" data-testid="portal-tab-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo]">Client Portal Links</h3>
              <p className="text-[10px] text-muted-foreground">Share read-only project views with clients</p>
            </div>
            <Button size="sm" onClick={() => { setLinkForm({ project_id: "", client_name: "", client_email: "", expires_days: "30" }); setShowCreateLink(true); }} data-testid="create-portal-link-btn">
              <Plus size={14} className="mr-1" /> New Link
            </Button>
          </div>

          {portalLinks.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-portal-links">
              <Link size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No portal links created</p>
              <p className="text-xs text-muted-foreground">Generate shareable links so clients can view project progress.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {portalLinks.map(link => {
                const baseUrl = window.location.origin;
                const portalUrl = `${baseUrl}/client-portal/${link.token}`;
                return (
                  <div key={link.id} className={`border rounded-md p-4 ${link.active ? "border-border" : "border-border/50 opacity-60"}`} data-testid={`portal-link-${link.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold">{link.project_name}</h4>
                          <Badge variant={link.active ? "default" : "outline"} className="text-[9px]">{link.active ? "Active" : "Revoked"}</Badge>
                        </div>
                        {link.client_name && <p className="text-xs text-muted-foreground">Client: <span className="font-medium text-foreground">{link.client_name}</span> {link.client_email && `(${link.client_email})`}</p>}
                        <div className="flex items-center gap-1 mt-2 bg-muted/50 rounded p-1.5">
                          <input readOnly value={portalUrl} className="flex-1 text-[10px] bg-transparent border-none outline-none text-muted-foreground" data-testid={`portal-url-${link.id}`} />
                          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Link copied"); }} data-testid={`copy-link-${link.id}`}>
                            <Copy size={12} />
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">Expires: {link.expires_at ? new Date(link.expires_at).toLocaleDateString() : "N/A"} | Created by {link.created_by_name}</p>
                      </div>
                      {link.active && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0" title="Revoke Link"
                          onClick={async () => {
                            try {
                              await API.delete(`/client-portal/links/${link.id}`);
                              setPortalLinks(prev => prev.map(l => l.id === link.id ? { ...l, active: false } : l));
                              toast.success("Link revoked");
                            } catch { toast.error("Failed to revoke"); }
                          }} data-testid={`revoke-link-${link.id}`}>
                          <Prohibit size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Create Link Dialog */}
          <Dialog open={showCreateLink} onOpenChange={setShowCreateLink}>
            <DialogContent className="max-w-md" data-testid="create-link-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">Share Project Portal</DialogTitle>
                <DialogDescription>Generate a shareable link for client access.</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await API.post("/client-portal/links", { ...linkForm, expires_days: parseInt(linkForm.expires_days) || 30 });
                  toast.success("Portal link created");
                  setShowCreateLink(false);
                  const { data } = await API.get("/client-portal/links");
                  setPortalLinks(data);
                } catch (err) { toast.error(err.response?.data?.detail || "Failed to create link"); }
              }} className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <Select value={linkForm.project_id} onValueChange={v => setLinkForm({...linkForm, project_id: v})}>
                    <SelectTrigger data-testid="link-project-select"><SelectValue placeholder="Select a project" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Client Name</Label><Input value={linkForm.client_name} onChange={e => setLinkForm({...linkForm, client_name: e.target.value})} placeholder="John Doe" data-testid="link-client-name" /></div>
                  <div><Label>Client Email</Label><Input type="email" value={linkForm.client_email} onChange={e => setLinkForm({...linkForm, client_email: e.target.value})} placeholder="client@example.com" data-testid="link-client-email" /></div>
                </div>
                <div>
                  <Label>Expires in (days)</Label>
                  <Select value={linkForm.expires_days} onValueChange={v => setLinkForm({...linkForm, expires_days: v})}>
                    <SelectTrigger data-testid="link-expires-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowCreateLink(false)}>Cancel</Button>
                  <Button type="submit" disabled={!linkForm.project_id} data-testid="submit-portal-link-btn">Generate Link</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="apikeys" className="mt-4" data-testid="apikeys-tab-content">
          {!hasFeature(user, "api_keys") ? <LockedFeature feature="api_keys" compact /> : (<>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo]">External API Keys</h3>
              <p className="text-xs text-muted-foreground">Manage API keys for external platforms to connect to the platform's file storage.</p>
            </div>
            <Button size="sm" onClick={() => { setShowCreateKey(true); setNewKeyResult(null); }} data-testid="create-api-key-btn">
              <Plus size={14} className="mr-1" /> Create API Key
            </Button>
          </div>

          {apiKeys.length > 0 ? (
            <div className="border border-border rounded-md overflow-hidden" data-testid="api-keys-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2.5 font-semibold">App Name</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Key</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Files</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden lg:table-cell">Requests</th>
                    <th className="text-left px-4 py-2.5 font-semibold hidden md:table-cell">Last Used</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                    <th className="text-center px-4 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {apiKeys.map(k => (
                    <tr key={k.id} className={`hover:bg-muted/30 transition-colors ${!k.active ? "opacity-50" : ""}`} data-testid={`api-key-row-${k.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-xs">{k.app_name}</p>
                        {k.description && <p className="text-[10px] text-muted-foreground">{k.description}</p>}
                        <p className="text-[10px] text-muted-foreground">by {k.created_by_name} | {fmtDate(k.created_at)}</p>
                      </td>
                      <td className="px-4 py-2.5 hidden md:table-cell font-mono text-xs text-muted-foreground">{k.key_preview}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs">{k.files_count || 0}</td>
                      <td className="px-4 py-2.5 hidden lg:table-cell text-xs">{k.request_count || 0}</td>
                      <td className="px-4 py-2.5 hidden md:table-cell text-xs text-muted-foreground">{k.last_used_at ? k.last_used_at.slice(0, 10) : "Never"}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${k.active ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400" : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"}`}>
                          {k.active ? "Active" : "Revoked"}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => viewKeyFiles(k.id)} data-testid={`view-files-${k.id}`}>Files</Button>
                          {k.active && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600" onClick={() => handleRevokeKey(k.id)} data-testid={`revoke-key-${k.id}`}>
                              <Prohibit size={12} className="mr-0.5" /> Revoke
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteKey(k.id)} title="Delete permanently" data-testid={`delete-key-${k.id}`}>
                            <Trash size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-api-keys">
              <Key size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No API keys created</p>
              <p className="text-xs text-muted-foreground">Create an API key to allow external platforms to upload and manage files.</p>
            </div>
          )}

          {/* API Docs snippet */}
          <div className="mt-6 border border-border rounded-md p-4 bg-muted/20" data-testid="api-docs-snippet">
            <h4 className="text-xs font-semibold font-[Chivo] mb-2">Quick Start</h4>
            <p className="text-[11px] text-muted-foreground mb-3">Use these endpoints from your external app. Authenticate with the <code className="bg-muted px-1 rounded">X-API-Key</code> header.</p>
            <div className="space-y-2 font-mono text-[11px]">
              <div className="bg-background rounded border border-border p-2">
                <span className="text-emerald-600 font-semibold">POST</span> <span className="text-muted-foreground">/api/external/files/upload</span>
                <span className="text-muted-foreground ml-2">— Upload file (multipart, max 50MB)</span>
              </div>
              <div className="bg-background rounded border border-border p-2">
                <span className="text-blue-600 font-semibold">GET</span> <span className="text-muted-foreground">/api/external/files</span>
                <span className="text-muted-foreground ml-2">— List files (?folder=invoices)</span>
              </div>
              <div className="bg-background rounded border border-border p-2">
                <span className="text-blue-600 font-semibold">GET</span> <span className="text-muted-foreground">/api/external/files/{'<id>'}/download</span>
                <span className="text-muted-foreground ml-2">— Download file</span>
              </div>
              <div className="bg-background rounded border border-border p-2">
                <span className="text-red-600 font-semibold">DELETE</span> <span className="text-muted-foreground">/api/external/files/{'<id>'}</span>
                <span className="text-muted-foreground ml-2">— Delete file</span>
              </div>
            </div>
          </div>

          {/* Create Key Dialog */}
          <Dialog open={showCreateKey} onOpenChange={(open) => { if (!open) { setShowCreateKey(false); setNewKeyResult(null); } }}>
            <DialogContent className="max-w-md" data-testid="create-key-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">{newKeyResult ? "API Key Created" : "Create API Key"}</DialogTitle>
                <DialogDescription>{newKeyResult ? "Copy this key now — it won't be shown again." : "Create an API key for an external application."}</DialogDescription>
              </DialogHeader>
              {!newKeyResult ? (
                <form onSubmit={handleCreateKey} className="space-y-4">
                  <div><Label>App Name *</Label><Input value={newKeyForm.app_name} onChange={e => setNewKeyForm({...newKeyForm, app_name: e.target.value})} required placeholder="e.g. Invoice Platform" data-testid="key-app-name" /></div>
                  <div><Label>Description</Label><Input value={newKeyForm.description} onChange={e => setNewKeyForm({...newKeyForm, description: e.target.value})} placeholder="What is this key for?" data-testid="key-description" /></div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setShowCreateKey(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-create-key">Create Key</Button>
                  </DialogFooter>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-muted border border-border rounded-md p-3" data-testid="new-key-display">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Your API Key</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono bg-background border border-border rounded px-2 py-1.5 break-all select-all">{newKeyResult.raw_key}</code>
                      <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => {
                        navigator.clipboard.writeText(newKeyResult.raw_key);
                        toast.success("API key copied to clipboard");
                      }} data-testid="copy-key-btn"><Copy size={14} /></Button>
                    </div>
                    <p className="text-[10px] text-destructive mt-2 font-medium">Save this key securely. It will not be shown again.</p>
                  </div>
                  <div className="bg-muted/50 border border-border rounded-md p-3">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Example Usage</p>
                    <code className="text-[11px] font-mono text-muted-foreground block whitespace-pre-wrap">
{`curl -X POST ${window.location.origin}/api/external/files/upload \\
  -H "X-API-Key: ${newKeyResult.raw_key}" \\
  -F "file=@document.pdf" \\
  -F "folder=invoices"`}
                    </code>
                  </div>
                  <DialogFooter>
                    <Button onClick={() => { setShowCreateKey(false); setNewKeyResult(null); }}>Done</Button>
                  </DialogFooter>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* View Files Dialog */}
          <Dialog open={!!showKeyFiles} onOpenChange={(open) => { if (!open) setShowKeyFiles(null); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="key-files-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">External Files</DialogTitle>
                <DialogDescription>{keyFiles.length} file(s) stored by this API key</DialogDescription>
              </DialogHeader>
              {keyFiles.length > 0 ? (
                <div className="divide-y divide-border" data-testid="key-files-list">
                  {keyFiles.map(f => (
                    <div key={f.id} className="flex items-center justify-between py-2 text-xs">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{f.file_name}</p>
                        <p className="text-muted-foreground">{f.folder || "root"} | {(f.size_bytes / 1024).toFixed(1)}KB | {fmtDate(f.created_at)}</p>
                        {f.tags?.length > 0 && <div className="flex gap-1 mt-0.5">{f.tags.map(t => <span key={t} className="px-1.5 py-0.5 rounded bg-muted text-[9px]">{t}</span>)}</div>}
                      </div>
                      <span className="text-muted-foreground ml-2">{f.content_type}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">No files uploaded with this key.</p>
              )}
            </DialogContent>
          </Dialog>
          </>)}
        </TabsContent>

        {/* ===== WEEKLY DIGEST ===== */}
        <TabsContent value="digest" className="mt-4" data-testid="digest-tab-content">
          <WeeklyDigestSection />
        </TabsContent>

        {/* ===== MARKUP MANAGEMENT ===== */}
        <TabsContent value="markup" className="mt-4" data-testid="markup-tab-content">
          <MarkupManagementSection />
        </TabsContent>

        <TabsContent value="pricing-calc" className="mt-4" data-testid="pricing-calc-tab-content">
          <PricingCalculatorSection />
        </TabsContent>

        <TabsContent value="escalation" className="mt-4" data-testid="escalation-tab-content">
          <EscalationSettingsSection suppliers={vendors} />
        </TabsContent>

        <TabsContent value="mfa" className="mt-4" data-testid="mfa-tab-content">
          <MfaSettingsSection />
        </TabsContent>

        <TabsContent value="language" className="mt-4" data-testid="language-tab-content">
          <div className="max-w-md">
            <h3 className="text-base font-semibold font-[Chivo] mb-1">Language</h3>
            <p className="text-xs text-muted-foreground mb-4">Choose the display language for the platform interface.</p>
            <div className="space-y-1">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => { setLanguage(lang.code); toast.success(`Language changed to ${lang.name}`); }}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm flex items-center justify-between transition-colors ${language === lang.code ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border text-muted-foreground hover:bg-muted"}`}
                  data-testid={`lang-option-${lang.code}`}
                >
                  <span>{lang.name}</span>
                  {language === lang.code && <Badge className="bg-primary text-primary-foreground text-[9px]">Active</Badge>}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sidebar" className="mt-4" data-testid="sidebar-tab-content">
          <SidebarMenuSettings />
        </TabsContent>

        <TabsContent value="vat" className="mt-4" data-testid="vat-tab-content">
          <VatSettingsSection />
        </TabsContent>

        <TabsContent value="bank-details" className="mt-4" data-testid="bank-details-tab-content">
          <BankDetailsSettingsSection />
        </TabsContent>

        <TabsContent value="email-sending" className="mt-4" data-testid="email-sending-tab-content">
          <EmailSendingSettings />
        </TabsContent>
        <TabsContent value="integrations" className="mt-4" data-testid="integrations-tab-content">
          <IntegrationsHub />
        </TabsContent>
        <TabsContent value="card-payments" className="mt-4 space-y-5" data-testid="card-payments-tab-content">
          <StripeConnectCard />
          <StripeKeysCard
            endpoint="/settings/stripe"
            variant="tenant"
            title="Or paste your own Stripe API keys"
            intro="Already have a Stripe account? Paste its keys and clients pay invoices by card straight into it. Without Connect or keys, invoices show bank-transfer details only — no card payments are taken on your behalf."
          />
        </TabsContent>
        <TabsContent value="quote-email" className="mt-4" data-testid="quote-email-tab-content">
          <QuoteEmailSettingsSection />
        </TabsContent>

        <TabsContent value="ack-email" className="mt-4" data-testid="ack-email-tab-content">
          <AcknowledgmentEmailSettings />
        </TabsContent>

        <TabsContent value="sla-watch" className="mt-4" data-testid="sla-watch-tab-content">
          <SlaWatchSettingsSection />
        </TabsContent>

        <TabsContent value="quote-sla" className="mt-4" data-testid="quote-sla-tab-content">
          <QuoteSlaSettingsSection />
        </TabsContent>

        <TabsContent value="sub-jobs" className="mt-4" data-testid="sub-jobs-tab-content">
          <SubJobsSettingsSection />
        </TabsContent>

        <TabsContent value="photo-qa" className="mt-4" data-testid="photo-qa-tab-content">
          <PhotoQaSettings />
        </TabsContent>

        <TabsContent value="email-inbox" className="mt-4" data-testid="email-inbox-tab-content">
          <InboxSettings inline />
        </TabsContent>

        <TabsContent value="booking" className="mt-4" data-testid="booking-tab-content">
          <BookingSettings />
        </TabsContent>

        <TabsContent value="resident-triage" className="mt-4" data-testid="resident-triage-tab-content">
          <ResidentTriageSettings />
        </TabsContent>

        <TabsContent value="permissions" className="mt-4" data-testid="permissions-tab-content">
          <UserPermissionsSection />
        </TabsContent>

        <TabsContent value="quote-followups" className="mt-4" data-testid="quote-followups-tab-content">
          <QuoteFollowupsSettingsSection />
        </TabsContent>

        <TabsContent value="cashflow-snapshot" className="mt-4" data-testid="cashflow-snapshot-tab-content">
          <CashflowSnapshotSettingsSection />
        </TabsContent>

        <TabsContent value="saas-platform" className="mt-4" data-testid="saas-platform-tab-content">
          <SaasPlatformSection />
        </TabsContent>
        </section>
      </Tabs>

      {/* Invite Team Member Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Invite Team Member</DialogTitle><DialogDescription>Send an invitation email. They'll set up their own password to join the platform.</DialogDescription></DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div><Label className="text-xs">Full Name *</Label><Input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} required placeholder="John Smith" data-testid="new-user-name" /></div>
            <div><Label className="text-xs">Email Address *</Label><Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} required placeholder="john@company.com" data-testid="new-user-email" /></div>
            <div>
              <Label className="text-xs">Role *</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="hs_manager">H&amp;S Manager</SelectItem>
                  <SelectItem value="hr_manager">HR Manager</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-new-user-btn"><PaperPlaneTilt size={14} className="mr-1" /> Send Invitation</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Edit User</DialogTitle><DialogDescription>Update user details and permissions.</DialogDescription></DialogHeader>
          {editUser && (
            <form onSubmit={handleEditUser} className="space-y-4">
              <div><Label>Name</Label><Input value={editUser.name} onChange={e => setEditUser({...editUser, name: e.target.value})} data-testid="edit-user-name" /></div>
              <div>
                <Label>Role</Label>
                {(() => {
                  const baseRole = editUser.role?.startsWith("custom:") ? editUser._baseRole : editUser.role;
                  const isAdminBaseEdit = ["super_admin", "admin", "project_manager"].includes(baseRole);
                  return (
                    <Select value={editUser.role} onValueChange={v => {
                      if (v.startsWith("custom:")) {
                        const r = customRoles.find(x => `custom:${x.id}` === v);
                        const isOps = r && (r.is_system || (r.name || "").trim().toLowerCase() === "operations director");
                        setEditUser({ ...editUser, role: v, ...(isOps ? { operations_director: true } : {}) });
                      } else {
                        setEditUser({ ...editUser, role: v });
                      }
                    }}>
                      <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Admin", "In-house", "Supplier"].map(g => {
                          const opts = ROLE_OPTIONS.filter(o => o.group === g);
                          return (
                            <div key={g}>
                              <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-wider font-bold text-muted-foreground">{g}</div>
                              {opts.map(o => <SelectItem key={o.value} value={o.value} data-testid={`edit-role-option-${o.value}`}>{o.label}</SelectItem>)}
                            </div>
                          );
                        })}
                        {isAdminBaseEdit && customRoles.length > 0 && (
                          <div>
                            <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Permission Roles</div>
                            {customRoles.map(r => (
                              <SelectItem key={r.id} value={`custom:${r.id}`} data-testid={`edit-role-option-custom-${r.id}`}>
                                {r.is_system ? "👑 " : ""}{r.name}
                              </SelectItem>
                            ))}
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  );
                })()}
                {editUser.role?.startsWith("custom:") && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Base role stays "{(editUser._baseRole || "").replace(/_/g, " ")}" — permission roles control which portal sections they can access.
                  </p>
                )}
              </div>
              {(["super_admin", "admin", "project_manager"].includes(editUser.role?.startsWith("custom:") ? editUser._baseRole : editUser.role)) && (
                <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5" data-testid="edit-user-ops-director-row">
                  <div>
                    <p className="text-sm font-medium">Operations Director</p>
                    <p className="text-[10px] text-muted-foreground">Grants access to the Staff Monitoring dashboard</p>
                  </div>
                  <Switch checked={!!editUser.operations_director} onCheckedChange={c => setEditUser({ ...editUser, operations_director: c })} data-testid="edit-user-ops-director" />
                </div>
              )}
              <div><Label>New Password (optional)</Label><Input type="password" value={editUser.newPassword} onChange={e => setEditUser({...editUser, newPassword: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Phone</Label><Input value={editUser.phone || ""} onChange={e => setEditUser({...editUser, phone: e.target.value})} /></div>
                <div><Label>Company</Label><Input value={editUser.company || ""} onChange={e => setEditUser({...editUser, company: e.target.value})} /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditUser(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-user-btn">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function WeeklyDigestSection() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await API.get("/scheduler/digest-preview");
      setPreview(data);
    } catch (e) { toast.error("Failed to load preview"); }
    finally { setLoadingPreview(false); }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data } = await API.post("/scheduler/send-digest");
      toast.success(`Digest sent to ${data.sent} team member${data.sent !== 1 ? "s" : ""}`);
      setLastSent(new Date().toLocaleString("en-GB"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send digest"); }
    finally { setSending(false); }
  };

  useEffect(() => { loadPreview(); }, []);

  const d = preview?.data;

  return (
    <div data-testid="weekly-digest-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold font-[Chivo]">Weekly Digest</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Automated Monday morning email sent to all admins & team members with a summary of the week ahead.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview} data-testid="digest-refresh">
            <ArrowsClockwise size={14} className={`mr-1 ${loadingPreview ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={handleSendNow} disabled={sending} data-testid="digest-send-now">
            <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending..." : "Send Now"}
          </Button>
        </div>
      </div>
      {lastSent && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mb-3">Last sent: {lastSent}</p>}

      {/* Schedule Info */}
      <div className="border border-border rounded-lg p-4 bg-card mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <EnvelopeSimple size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold">Schedule: Every Monday at 7:00 AM UTC</p>
            <p className="text-xs text-muted-foreground">Sent to all super admins, admins, project managers, and team members (unless opted out)</p>
          </div>
        </div>
      </div>

      {/* Preview Data Summary */}
      {d && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold font-[Chivo]">Preview: Week of {d.week_start}</h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="digest-preview-stats">
            <PreviewStatCard label="Active Projects" value={d.active_projects?.length || 0} color="blue" />
            <PreviewStatCard label="Overdue Tasks" value={d.overdue_count || 0} color={d.overdue_count > 0 ? "red" : "green"} />
            <PreviewStatCard label="Expiring Docs" value={d.expiring_count || 0} color={d.expiring_count > 0 ? "amber" : "green"} />
            <PreviewStatCard label="Pending Quotes" value={d.pending_quotes?.length || 0} color="violet" />
          </div>

          {/* Overdue Tasks Preview */}
          {d.overdue_tasks?.length > 0 && (
            <div className="border border-red-200 dark:border-red-800 rounded-lg overflow-hidden" data-testid="digest-overdue-preview">
              <div className="bg-red-50 dark:bg-red-900/20 px-4 py-2">
                <h4 className="text-xs font-semibold text-red-700 dark:text-red-400">Overdue Tasks ({d.overdue_count})</h4>
              </div>
              <div className="divide-y divide-border">
                {d.overdue_tasks.slice(0, 5).map((t, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-xs">
                    <div>
                      <span className="font-medium">{t.title}</span>
                      <span className="text-muted-foreground ml-2">{t.project_name}</span>
                    </div>
                    <Badge variant="destructive" className="text-[10px]">{t.days_overdue}d overdue</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expiring Docs Preview */}
          {d.expiring_docs?.length > 0 && (
            <div className="border border-amber-200 dark:border-amber-800 rounded-lg overflow-hidden" data-testid="digest-expiring-preview">
              <div className="bg-amber-50 dark:bg-amber-900/20 px-4 py-2">
                <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400">Expiring Documents ({d.expiring_count})</h4>
              </div>
              <div className="divide-y divide-border">
                {d.expiring_docs.slice(0, 5).map((doc, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-xs">
                    <div>
                      <span className="font-medium">{doc.doc_type?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                      <span className="text-muted-foreground ml-2">{doc.company_name}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 dark:text-amber-400">{doc.days_until_expiry}d left</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Quotes Preview */}
          {d.pending_quotes?.length > 0 && (
            <div className="border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden" data-testid="digest-quotes-preview">
              <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2">
                <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-400">Pending Quotes ({d.pending_quotes.length})</h4>
              </div>
              <div className="divide-y divide-border">
                {d.pending_quotes.slice(0, 5).map((q, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-xs">
                    <div>
                      <span className="font-mono text-muted-foreground mr-2">{q.reference}</span>
                      <span className="font-medium">{q.title}</span>
                    </div>
                    <span className="text-muted-foreground">{q.quotes?.length || 0} quote{(q.quotes?.length || 0) !== 1 ? "s" : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email HTML Preview Toggle */}
          <details className="border border-border rounded-lg overflow-hidden" data-testid="digest-html-preview">
            <summary className="px-4 py-3 bg-muted/30 cursor-pointer text-xs font-semibold hover:bg-muted/50 transition-colors">Preview Email HTML</summary>
            <div className="p-4 bg-white dark:bg-card overflow-auto max-h-[500px]">
              <iframe
                title="Digest Preview"
                srcDoc={preview?.html || ""}
                className="w-full h-[400px] border-0 rounded"
                sandbox=""
              />
            </div>
          </details>

          {/* Activity Stats */}
          <div className="border border-border rounded-lg p-4 bg-muted/20">
            <h4 className="text-xs font-semibold mb-2">Week-over-Week Activity</h4>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-lg font-bold font-[Chivo]">{d.stats?.completed_this_week || 0}</p>
                <p className="text-[10px] text-muted-foreground">Tasks Completed</p>
              </div>
              <div>
                <p className="text-lg font-bold font-[Chivo]">{d.stats?.new_requests || 0}</p>
                <p className="text-[10px] text-muted-foreground">New Requests</p>
              </div>
              <div>
                <p className="text-lg font-bold font-[Chivo]">{d.stats?.new_messages || 0}</p>
                <p className="text-[10px] text-muted-foreground">Messages</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!d && !loadingPreview && (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <EnvelopeSimple size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Click "Refresh" to preview the digest data.</p>
        </div>
      )}
      {loadingPreview && (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
}

function PreviewStatCard({ label, value, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
    red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400",
    green: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400",
    amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400",
    violet: "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400",
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`} data-testid={`digest-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="text-2xl font-bold font-[Chivo]">{value}</p>
      <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5 opacity-80">{label}</p>
    </div>
  );
}


const MARKUP_OPTS = [0, 5, 7.5, 10, 12.5, 15, 17.5, 20, 22.5, 25, 27.5, 30, 32.5, 35, 37.5, 40, 45, 50];

function MarkupManagementSection() {
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", markup_a_pct: 30, markup_b_pct: 10, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get("/markup-templates").then(({ data }) => setTemplates(data.templates || [])),
      API.get("/markup-analytics").then(({ data }) => setAnalytics(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/markup-templates/${editingId}`, form);
        toast.success("Template updated");
      } else {
        await API.post("/markup-templates", form);
        toast.success("Template created");
      }
      setShowCreate(false);
      setEditingId(null);
      setForm({ name: "", markup_a_pct: 30, markup_b_pct: 10, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/markup-templates/${id}`);
      toast.success("Template deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, markup_a_pct: t.markup_a_pct, markup_b_pct: t.markup_b_pct, markup_a_label: t.markup_a_label, markup_b_label: t.markup_b_label });
    setShowCreate(true);
  };

  const s = analytics?.summary || {};
  const patterns = analytics?.by_pattern || [];
  const quotes = analytics?.quotes || [];

  // Example preview calc
  const prevCost = 1000;
  const prevMA = prevCost * ((form.markup_a_pct || 0) / 100);
  const prevSub = prevCost + prevMA;
  const prevMB = prevSub * ((form.markup_b_pct || 0) / 100);
  const prevTotal = prevSub + prevMB;

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="markup-management-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-semibold font-[Chivo]">Markup Management</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Create and manage reusable markup templates. View profit analytics across all marked-up quotes.</p>
        </div>
        <Button size="sm" onClick={() => { setEditingId(null); setForm({ name: "", markup_a_pct: 30, markup_b_pct: 10, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up" }); setShowCreate(true); }} data-testid="markup-create-btn">
          <Plus size={14} className="mr-1" /> New Template
        </Button>
      </div>

      {/* Profit Analytics Cards */}
      {s.total_quotes_with_markup > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Profit Overview</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="markup-profit-overview">
            <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 bg-emerald-50 dark:bg-emerald-900/20" data-testid="markup-stat-total-profit">
              <TrendUp size={16} className="text-emerald-600 mb-1" />
              <p className="text-xl font-bold font-[Chivo] text-emerald-700 dark:text-emerald-400">£{s.total_profit?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-500 font-medium">Our Profit</p>
            </div>
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="markup-stat-total-cost">
              <p className="text-xl font-bold font-[Chivo]">£{s.total_cost?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Total Cost</p>
            </div>
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="markup-stat-client-price">
              <p className="text-xl font-bold font-[Chivo]">£{s.total_client_price?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Client Price</p>
            </div>
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="markup-stat-avg-margin">
              <p className="text-xl font-bold font-[Chivo]">{s.avg_margin_pct}%</p>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Avg Margin</p>
            </div>
          </div>
        </div>
      )}

      {/* Markup Pattern Breakdown */}
      {patterns.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Profit by Markup Pattern</h3>
          <div className="border border-border rounded-lg divide-y divide-border overflow-hidden" data-testid="markup-pattern-table">
            <div className="grid grid-cols-4 gap-4 px-4 py-2 bg-muted/30 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Pattern</span><span className="text-right">Quotes</span><span className="text-right">Total Cost</span><span className="text-right">Our Profit</span>
            </div>
            {patterns.map(p => (
              <div key={p.pattern} className="grid grid-cols-4 gap-4 px-4 py-2.5 text-xs hover:bg-muted/10 transition-colors">
                <span className="font-semibold font-mono">{p.pattern}</span>
                <span className="text-right text-muted-foreground">{p.count}</span>
                <span className="text-right font-mono">£{p.total_cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                <span className="text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">£{p.total_profit.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Templates List */}
      <div className="mb-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Saved Templates ({templates.length})</h3>
        {templates.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-border rounded-lg">
            <CurrencyGbp size={32} className="mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-1">No markup templates yet</p>
            <p className="text-xs text-muted-foreground mb-3">Create your first template to streamline quote markup.</p>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus size={14} className="mr-1" /> Create Template
            </Button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="markup-templates-grid">
            {templates.map(t => {
              const exCost = 1000;
              const exMA = exCost * (t.markup_a_pct / 100);
              const exSub = exCost + exMA;
              const exMB = exSub * (t.markup_b_pct / 100);
              const exTotal = exSub + exMB;
              return (
                <div key={t.id} className="border border-border rounded-lg p-4 bg-card card-lift" data-testid={`markup-template-${t.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold">{t.name}</h4>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => startEdit(t)} data-testid={`markup-edit-${t.id}`}><PencilSimple size={12} /></Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(t.id)} data-testid={`markup-delete-${t.id}`}><Trash size={12} /></Button>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.markup_a_label}</span>
                      <span className="font-semibold text-primary">{t.markup_a_pct}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t.markup_b_label}</span>
                      <span className="font-semibold text-violet-600 dark:text-violet-400">{t.markup_b_pct}%</span>
                    </div>
                    <div className="border-t border-border pt-1.5 mt-1.5">
                      <p className="text-[10px] text-muted-foreground mb-1">Example on £1,000:</p>
                      <div className="flex justify-between text-[10px]">
                        <span>Cost → Subtotal → Total</span>
                        <span className="font-mono font-medium">£{exCost.toLocaleString()} → £{exSub.toLocaleString()} → £{exTotal.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-[10px] mt-0.5">
                        <span className="text-emerald-600 dark:text-emerald-400">Our Profit</span>
                        <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">£{exMA.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detailed Quotes Table */}
      {quotes.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">All Marked-Up Quotes ({quotes.length})</h3>
          <div className="border border-border rounded-lg overflow-x-auto" data-testid="markup-quotes-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/30 border-b border-border">
                  <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Reference</th>
                  <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Client</th>
                  <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Supplier</th>
                  <th className="px-3 py-2 text-right font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Cost</th>
                  <th className="px-3 py-2 text-center font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Markup A</th>
                  <th className="px-3 py-2 text-center font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Markup B</th>
                  <th className="px-3 py-2 text-right font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">Total</th>
                  <th className="px-3 py-2 text-right font-semibold text-[10px] uppercase tracking-wider text-emerald-600">Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {quotes.map((q, i) => (
                  <tr key={i} className="hover:bg-muted/10 transition-colors">
                    <td className="px-3 py-2 font-mono">{q.jr_reference}</td>
                    <td className="px-3 py-2 text-muted-foreground">{q.client_name || "-"}</td>
                    <td className="px-3 py-2">{q.vendor_name}</td>
                    <td className="px-3 py-2 text-right font-mono">£{q.cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-center"><span className="text-primary font-medium">{q.markup_a_pct}%</span> <span className="text-muted-foreground">(£{q.markup_a_amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })})</span></td>
                    <td className="px-3 py-2 text-center"><span className="text-violet-600 dark:text-violet-400 font-medium">{q.markup_b_pct}%</span> <span className="text-muted-foreground">(£{q.markup_b_amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })})</span></td>
                    <td className="px-3 py-2 text-right font-mono font-semibold">£{q.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">£{q.profit.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/20 border-t-2 border-border font-semibold">
                  <td colSpan={3} className="px-3 py-2 text-xs uppercase">Totals</td>
                  <td className="px-3 py-2 text-right font-mono">£{s.total_cost?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-center font-mono text-primary">£{s.total_markup_a?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-center font-mono text-violet-600 dark:text-violet-400">£{s.total_markup_b?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-right font-mono">£{s.total_client_price?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-600 dark:text-emerald-400">£{s.total_profit?.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-md" data-testid="markup-template-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">{editingId ? "Edit Template" : "Create Markup Template"}</DialogTitle>
            <DialogDescription>Define the markup percentages and labels for this template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Template Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard (30% / 10%)" className="mt-1" data-testid="markup-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Markup A Label</Label>
                <Input value={form.markup_a_label} onChange={e => setForm(p => ({ ...p, markup_a_label: e.target.value }))} className="mt-1 text-sm" data-testid="markup-form-a-label" />
              </div>
              <div>
                <Label className="text-xs">Markup A (%)</Label>
                <Select value={String(form.markup_a_pct)} onValueChange={v => setForm(p => ({ ...p, markup_a_pct: parseFloat(v) }))}>
                  <SelectTrigger className="mt-1" data-testid="markup-form-a-pct"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MARKUP_OPTS.map(o => <SelectItem key={o} value={String(o)}>{o}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Markup B Label</Label>
                <Input value={form.markup_b_label} onChange={e => setForm(p => ({ ...p, markup_b_label: e.target.value }))} className="mt-1 text-sm" data-testid="markup-form-b-label" />
              </div>
              <div>
                <Label className="text-xs">Markup B (%)</Label>
                <Select value={String(form.markup_b_pct)} onValueChange={v => setForm(p => ({ ...p, markup_b_pct: parseFloat(v) }))}>
                  <SelectTrigger className="mt-1" data-testid="markup-form-b-pct"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MARKUP_OPTS.map(o => <SelectItem key={o} value={String(o)}>{o}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview on £1,000 */}
            <div className="border border-border rounded-md overflow-hidden text-xs">
              <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Preview on £1,000</p>
              </div>
              <div className="px-3 py-2 space-y-1">
                <div className="flex justify-between"><span>Cost</span><span className="font-mono">£{prevCost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></div>
                {form.markup_a_pct > 0 && (
                  <>
                    <div className="flex justify-between text-primary"><span>+ {form.markup_a_label} ({form.markup_a_pct}%)</span><span className="font-mono">£{prevMA.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></div>
                    <div className="flex justify-between border-t border-border/50 pt-1 font-semibold"><span>Subtotal</span><span className="font-mono">£{prevSub.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></div>
                  </>
                )}
                {form.markup_b_pct > 0 && (
                  <div className="flex justify-between text-violet-600 dark:text-violet-400"><span>+ {form.markup_b_label} ({form.markup_b_pct}%)</span><span className="font-mono">£{prevMB.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></div>
                )}
                <div className="flex justify-between border-t border-border pt-1 font-bold"><span>Total</span><span className="font-mono">£{prevTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span></div>
              </div>
              {form.markup_a_pct > 0 && (
                <div className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800 flex justify-between items-center">
                  <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">Our Profit</span>
                  <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400">£{prevMA.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditingId(null); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="markup-form-save">
              {saving ? "Saving..." : editingId ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EscalationSettingsSection({ suppliers }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    API.get("/settings/escalation")
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ enabled: true, escalation_emails: [], auto_reassign: false, fallback_vendor_ids: [], grace_minutes: 30 }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/escalation", config);
      toast.success("Escalation settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    if (!newEmail.trim() || !newEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    if (config.escalation_emails.includes(newEmail.trim())) { toast.error("Already added"); return; }
    setConfig({ ...config, escalation_emails: [...config.escalation_emails, newEmail.trim()] });
    setNewEmail("");
  };

  const removeEmail = (email) => {
    setConfig({ ...config, escalation_emails: config.escalation_emails.filter(e => e !== email) });
  };

  const addFallbackSupplier = (supplierId) => {
    if (supplierId === "none" || config.fallback_vendor_ids.includes(supplierId)) return;
    setConfig({ ...config, fallback_vendor_ids: [...config.fallback_vendor_ids, supplierId] });
  };

  const removeFallbackSupplier = (supplierId) => {
    setConfig({ ...config, fallback_vendor_ids: config.fallback_vendor_ids.filter(v => v !== supplierId) });
  };

  if (loading || !config) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="escalation-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-8 w-8 rounded-lg bg-red-600 flex items-center justify-center"><Lightning size={16} className="text-white" weight="fill" /></div>
        <div>
          <h2 className="text-base font-semibold font-[Chivo]">Emergency Escalation</h2>
          <p className="text-xs text-muted-foreground">Configure automatic escalation when emergency jobs breach their SLA</p>
        </div>
      </div>

      <SlaAlertSettingsCard />

      {/* Enable/Disable */}
      <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/20" data-testid="escalation-toggle-section">
        <div>
          <p className="text-sm font-medium">Auto-Escalation</p>
          <p className="text-[10px] text-muted-foreground">Automatically notify managers when SLA is breached</p>
        </div>
        <Switch checked={config.enabled} onCheckedChange={v => setConfig({ ...config, enabled: v })} data-testid="escalation-enabled-toggle" />
      </div>

      {config.enabled && (
        <div className="space-y-5">
          {/* Grace Period */}
          <div className="border border-border rounded-lg p-4">
            <Label className="text-xs font-semibold">Grace Period (minutes after SLA breach)</Label>
            <p className="text-[10px] text-muted-foreground mb-2">How long after SLA deadline to wait before escalating</p>
            <Select value={String(config.grace_minutes)} onValueChange={v => setConfig({ ...config, grace_minutes: parseInt(v) })}>
              <SelectTrigger className="w-48" data-testid="escalation-grace-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Immediately (0 min)</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Escalation Contacts */}
          <div className="border border-border rounded-lg p-4" data-testid="escalation-contacts">
            <Label className="text-xs font-semibold">Escalation Contacts</Label>
            <p className="text-[10px] text-muted-foreground mb-3">Email addresses to notify on SLA breach. If empty, all admins are notified.</p>
            <div className="flex gap-2 mb-3">
              <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="manager@fixlr.io" className="flex-1 h-8 text-xs" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addEmail())} data-testid="escalation-email-input" />
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addEmail} data-testid="escalation-add-email">Add</Button>
            </div>
            {config.escalation_emails.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {config.escalation_emails.map(email => (
                  <div key={email} className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs">
                    <EnvelopeSimple size={10} /> {email}
                    <button onClick={() => removeEmail(email)} className="ml-1 text-destructive hover:text-destructive/80"><X size={10} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground italic">No contacts configured — all admin users will be notified</p>
            )}
          </div>

          {/* Auto-Reassignment */}
          <div className="border border-border rounded-lg p-4" data-testid="auto-reassign-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-xs font-semibold">Auto-Reassign to Fallback Supplier</Label>
                <p className="text-[10px] text-muted-foreground">If an emergency is still "Open" when SLA breaches, auto-reassign to a backup supplier</p>
              </div>
              <Switch checked={config.auto_reassign} onCheckedChange={v => setConfig({ ...config, auto_reassign: v })} data-testid="auto-reassign-toggle" />
            </div>

            {config.auto_reassign && (
              <div className="mt-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Fallback Supplier List (Priority Order)</Label>
                <p className="text-[10px] text-muted-foreground mb-2">First available supplier in the list will be assigned</p>

                {config.fallback_vendor_ids.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {config.fallback_vendor_ids.map((vid, idx) => {
                      const vnd = suppliers.find(v => v.id === vid);
                      return (
                        <div key={vid} className="flex items-center justify-between px-3 py-2 border border-border rounded-md bg-card" data-testid={`fallback-vendor-${idx}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-muted-foreground w-5">{idx + 1}.</span>
                            <span className="text-xs font-medium">{vnd?.company_name || vnd?.name || vid}</span>
                            {vnd?.trade && <span className="text-[10px] text-muted-foreground">— {vnd.trade}</span>}
                          </div>
                          <button onClick={() => removeFallbackSupplier(vid)} className="text-destructive hover:text-destructive/80"><X size={12} /></button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Select onValueChange={addFallbackSupplier}>
                  <SelectTrigger className="text-xs h-8" data-testid="fallback-vendor-select"><SelectValue placeholder="Add fallback supplier..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select supplier...</SelectItem>
                    {suppliers.filter(v => !config.fallback_vendor_ids.includes(v.id)).map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name || v.company_name} — {v.trade || "General"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4">
            <h3 className="text-xs font-bold flex items-center gap-1.5 mb-2"><Warning size={14} className="text-amber-600" /> How Escalation Works</h3>
            <ul className="text-[10px] text-muted-foreground space-y-1.5 list-disc ml-4">
              <li>System checks every 5 minutes for SLA-breached emergency jobs</li>
              <li>Jobs that have breached SLA + grace period trigger escalation</li>
              <li>Escalation contacts receive a high-importance email notification</li>
              <li>All admin users get an in-app notification</li>
              {config.auto_reassign && <li>If the job is still "Open" (not even acknowledged), it will be auto-reassigned to the first available fallback supplier</li>}
              <li>Each job is only escalated once (marked as escalated after first trigger)</li>
            </ul>
          </div>
        </div>
      )}

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-escalation-btn">
        {saving ? "Saving..." : "Save Escalation Settings"}
      </Button>
    </div>
  );
}


function MfaSettingsSection() {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/mfa/status").then(({ data }) => setMfaStatus(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startSetup = async () => {
    try {
      const { data } = await API.post("/mfa/setup");
      setSetupData(data);
      setShowSetup(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start MFA setup");
    }
  };

  const verifySetup = async () => {
    if (!otpCode || otpCode.length < 6) return;
    setSaving(true);
    try {
      await API.post("/mfa/verify-setup", { otp_code: otpCode, secret: setupData.secret, backup_codes: setupData.backup_codes });
      toast.success("MFA enabled successfully!");
      setShowSetup(false);
      setShowBackupCodes(true);
      setMfaStatus({ mfa_enabled: true, backup_codes_remaining: setupData.backup_codes.length });
      setOtpCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const disableMfa = async () => {
    if (!disableCode) return;
    setSaving(true);
    try {
      await API.post("/mfa/disable", { otp_code: disableCode });
      toast.success("MFA disabled");
      setShowDisable(false);
      setMfaStatus({ mfa_enabled: false, backup_codes_remaining: 0 });
      setDisableCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading MFA status...</p>;

  return (
    <div className="space-y-6" data-testid="mfa-settings-section">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Two-Factor Authentication (2FA)</h3>
        <p className="text-xs text-muted-foreground">Add an extra layer of security using a TOTP authenticator app (Google Authenticator, Authy, etc.)</p>
      </div>

      <div className="border border-border rounded-lg p-4 bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${mfaStatus?.mfa_enabled ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-muted"}`}>
              <ShieldCheck size={20} className={mfaStatus?.mfa_enabled ? "text-emerald-600" : "text-muted-foreground"} weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-medium">{mfaStatus?.mfa_enabled ? "MFA is enabled" : "MFA is not enabled"}</p>
              <p className="text-[10px] text-muted-foreground">
                {mfaStatus?.mfa_enabled ? `${mfaStatus.backup_codes_remaining} backup code(s) remaining` : "Your account uses password-only authentication"}
              </p>
            </div>
          </div>
          {mfaStatus?.mfa_enabled ? (
            <Button variant="outline" size="sm" className="text-destructive border-destructive/50 hover:bg-destructive/10" onClick={() => setShowDisable(true)} data-testid="disable-mfa-btn">
              Disable MFA
            </Button>
          ) : (
            <Button onClick={startSetup} data-testid="enable-mfa-btn">Enable MFA</Button>
          )}
        </div>
      </div>

      {/* Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="max-w-md" data-testid="mfa-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>Scan the QR code with your authenticator app, then enter the 6-digit code to verify.</DialogDescription>
          </DialogHeader>
          {setupData && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={setupData.qr_code} alt="QR Code" className="h-48 w-48 rounded-md border border-border" data-testid="mfa-qr-code" />
              </div>
              <div className="text-center">
                <p className="text-[10px] text-muted-foreground mb-1">Can't scan? Enter this key manually:</p>
                <code className="text-xs font-mono bg-muted px-2 py-1 rounded select-all" data-testid="mfa-secret-key">{setupData.secret}</code>
              </div>
              <div>
                <Label>Enter 6-digit code from your app</Label>
                <Input
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg tracking-widest font-mono"
                  maxLength={6}
                  data-testid="mfa-setup-code-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetup(false)}>Cancel</Button>
            <Button onClick={verifySetup} disabled={saving || otpCode.length < 6} data-testid="mfa-setup-verify-btn">
              {saving ? "Verifying..." : "Verify & Enable"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-sm" data-testid="mfa-backup-codes-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] text-emerald-600">MFA Enabled!</DialogTitle>
            <DialogDescription>Save these backup codes somewhere safe. Each can be used once if you lose access to your authenticator.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 p-3 bg-muted rounded-md">
            {setupData?.backup_codes?.map((code, i) => (
              <code key={i} className="text-xs font-mono text-center py-1" data-testid={`backup-code-${i}`}>{code}</code>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const text = setupData?.backup_codes?.join("\n") || "";
                navigator.clipboard.writeText(text);
                toast.success("Backup codes copied to clipboard");
              }}
              data-testid="copy-backup-codes-btn"
            >
              <Copy size={14} className="mr-1" /> Copy All
            </Button>
            <Button onClick={() => setShowBackupCodes(false)} data-testid="close-backup-codes-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisable} onOpenChange={setShowDisable}>
        <DialogContent className="max-w-sm" data-testid="mfa-disable-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo] text-destructive">Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>Enter your current authenticator code to disable MFA.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Authentication Code</Label>
            <Input
              value={disableCode}
              onChange={e => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="text-center text-lg tracking-widest font-mono"
              maxLength={6}
              data-testid="mfa-disable-code-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable(false)}>Cancel</Button>
            <Button variant="destructive" onClick={disableMfa} disabled={saving || disableCode.length < 6} data-testid="mfa-disable-confirm-btn">
              {saving ? "Disabling..." : "Disable MFA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VatSettingsSection() {
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState(20);
  const [vatNumber, setVatNumber] = useState("");
  const [vatLabel, setVatLabel] = useState("VAT");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/settings/vat")
      .then(({ data }) => {
        setVatEnabled(data.vat_enabled || false);
        setVatRate(data.vat_rate || 20);
        setVatNumber(data.vat_number || "");
        setVatLabel(data.vat_label || "VAT");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/vat", { vat_enabled: vatEnabled, vat_rate: vatRate, vat_number: vatNumber, vat_label: vatLabel });
      toast.success("VAT settings saved");
    } catch { toast.error("Failed to save VAT settings"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-lg space-y-5" data-testid="vat-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">VAT Settings</h3>
        <p className="text-xs text-muted-foreground">Configure VAT to be applied automatically on quotes and invoices.</p>
      </div>

      <div className="flex items-center justify-between border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Enable VAT</p>
          <p className="text-[10px] text-muted-foreground">Automatically apply VAT to all new quotes</p>
        </div>
        <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} data-testid="vat-enabled-toggle" />
      </div>

      {vatEnabled && (
        <div className="space-y-3 border border-border rounded-lg p-4">
          <div>
            <Label className="text-xs">VAT Rate (%)</Label>
            <Input type="number" step="0.1" value={vatRate} onChange={e => setVatRate(parseFloat(e.target.value) || 0)} className="w-32 mt-1" data-testid="vat-rate-input" />
          </div>
          <div>
            <Label className="text-xs">VAT Registration Number</Label>
            <Input value={vatNumber} onChange={e => setVatNumber(e.target.value)} placeholder="GB 123 4567 89" className="mt-1" data-testid="vat-number-input" />
          </div>
          <div>
            <Label className="text-xs">VAT Label</Label>
            <Input value={vatLabel} onChange={e => setVatLabel(e.target.value)} placeholder="VAT" className="w-32 mt-1" data-testid="vat-label-input" />
          </div>
        </div>
      )}

      <Button onClick={handleSave} disabled={saving} data-testid="save-vat-btn">
        {saving ? "Saving..." : "Save VAT Settings"}
      </Button>
    </div>
  );
}


function BankDetailsSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [accountName, setAccountName] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [referenceLabel, setReferenceLabel] = useState("");
  const [paymentInstructions, setPaymentInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/settings/bank-details")
      .then(({ data }) => {
        setEnabled(!!data.enabled);
        setAccountName(data.account_name || "");
        setSortCode(data.sort_code || "");
        setAccountNumber(data.account_number || "");
        setBankName(data.bank_name || "");
        setReferenceLabel(data.reference_label || "");
        setPaymentInstructions(data.payment_instructions || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatSortCode = (v) => {
    const digits = v.replace(/\D/g, "").slice(0, 6);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/bank-details", {
        enabled,
        account_name: accountName,
        sort_code: sortCode,
        account_number: accountNumber,
        bank_name: bankName,
        reference_label: referenceLabel,
        payment_instructions: paymentInstructions,
      });
      toast.success("Bank details saved");
    } catch { toast.error("Failed to save bank details"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-lg space-y-5" data-testid="bank-details-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Bank Payment Details</h3>
        <p className="text-xs text-muted-foreground">Configure default bank account details that will appear on every new invoice. You can still edit them per-invoice if needed.</p>
      </div>

      <div className="flex items-center justify-between border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Show bank details on invoices</p>
          <p className="text-[10px] text-muted-foreground">Auto-fills the bank section on each new invoice and prints it on the PDF.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="bank-details-enabled-toggle" />
      </div>

      <div className="space-y-3 border border-border rounded-lg p-4">
        <div>
          <Label className="text-xs">Account Name *</Label>
          <Input value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="Your Company Ltd" className="mt-1" data-testid="bank-account-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Sort Code *</Label>
            <Input value={sortCode} onChange={e => setSortCode(formatSortCode(e.target.value))} placeholder="00-00-00" maxLength={8} className="mt-1" data-testid="bank-sort-code-input" />
          </div>
          <div>
            <Label className="text-xs">Account Number *</Label>
            <Input value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 8))} placeholder="12345678" maxLength={8} className="mt-1" data-testid="bank-account-number-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Bank Name (optional)</Label>
          <Input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Barclays" className="mt-1" data-testid="bank-name-input" />
        </div>
        <div>
          <Label className="text-xs">Payment Reference (optional)</Label>
          <Input value={referenceLabel} onChange={e => setReferenceLabel(e.target.value)} placeholder="Leave blank to use the invoice reference" className="mt-1" data-testid="bank-reference-input" />
          <p className="text-[10px] text-muted-foreground mt-1">If blank, the invoice reference (e.g. INV-A1B2C3D4) will be used as the payment reference on the PDF.</p>
        </div>
        <div>
          <Label className="text-xs">Payment Instructions (optional)</Label>
          <Textarea value={paymentInstructions} onChange={e => setPaymentInstructions(e.target.value)} rows={3} placeholder={"e.g. Please quote the invoice reference on your transfer.\nBACS payments typically take 1–3 working days to clear.\nFor card payments, please contact us on 020 0000 0000."} className="mt-1 text-sm" data-testid="bank-payment-instructions-input" />
          <p className="text-[10px] text-muted-foreground mt-1">Shown on the invoice PDF directly under the bank details box. Use it for BACS turnaround notes, payment-reference reminders, or any payment guidance.</p>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="save-bank-details-btn">
        {saving ? "Saving..." : "Save Bank Details"}
      </Button>
    </div>
  );
}


function QuoteEmailSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [defaultSubject, setDefaultSubject] = useState("");
  const [defaultMessage, setDefaultMessage] = useState("");
  const [bccAdmin, setBccAdmin] = useState(true);
  const [replyTo, setReplyTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/settings/quote-email")
      .then(({ data }) => {
        setEnabled(!!data.enabled);
        setDefaultSubject(data.default_subject || "");
        setDefaultMessage(data.default_message || "");
        setBccAdmin(data.bcc_admin !== false);
        setReplyTo(data.reply_to || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/quote-email", {
        enabled,
        default_subject: defaultSubject,
        default_message: defaultMessage,
        bcc_admin: bccAdmin,
        reply_to: replyTo,
      });
      toast.success("Quote email settings saved");
    } catch { toast.error("Failed to save quote email settings"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="quote-email-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Email Quotes from Platform</h3>
        <p className="text-xs text-muted-foreground">Optional white-label feature. When enabled, a "Send Email" button appears on each quote so you can deliver the branded PDF directly to your client.</p>
      </div>

      <div className="flex items-center justify-between border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Enable Email Quotes</p>
          <p className="text-[10px] text-muted-foreground">Off by default — turn on per tenant</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="quote-email-enabled-toggle" />
      </div>

      {enabled && (
        <div className="space-y-4 border border-border rounded-lg p-4">
          <div>
            <Label className="text-xs font-semibold">Default Subject</Label>
            <Input value={defaultSubject} onChange={e => setDefaultSubject(e.target.value)} placeholder="Your quote {reference} from {company}" className="mt-1" data-testid="quote-email-subject-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Default Message</Label>
            <Textarea value={defaultMessage} onChange={e => setDefaultMessage(e.target.value)} rows={8} className="mt-1 text-xs font-mono" data-testid="quote-email-message-input" />
            <p className="text-[10px] text-muted-foreground mt-1">
              Available placeholders: <code className="bg-muted px-1 rounded">{"{reference}"}</code>, <code className="bg-muted px-1 rounded">{"{title}"}</code>, <code className="bg-muted px-1 rounded">{"{client_name}"}</code>, <code className="bg-muted px-1 rounded">{"{company}"}</code>, <code className="bg-muted px-1 rounded">{"{total}"}</code>, <code className="bg-muted px-1 rounded">{"{validity_days}"}</code>, <code className="bg-muted px-1 rounded">{"{sender_name}"}</code>
            </p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">BCC the sender</p>
              <p className="text-[10px] text-muted-foreground">Send a copy to the admin who clicks "Send"</p>
            </div>
            <Switch checked={bccAdmin} onCheckedChange={setBccAdmin} data-testid="quote-email-bcc-toggle" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Reply-To (optional)</Label>
            <Input type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder="quotes@your-company.com" className="mt-1" data-testid="quote-email-replyto-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Overrides the workspace Reply-To from Settings → Email Sending for quote e-mails only.</p>
          </div>
        </div>
      )}

      <Button onClick={handleSave} disabled={saving} data-testid="save-quote-email-btn">
        {saving ? "Saving..." : "Save Settings"}
      </Button>
    </div>
  );
}


function SlaWatchSettingsSection() {
  const [enabled, setEnabled] = useState(false);
  const [floorMinutes, setFloorMinutes] = useState(180);
  const [pctIncrease, setPctIncrease] = useState(100);
  const [minRecentN, setMinRecentN] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingRun, setTestingRun] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const loadAlerts = () => {
    API.get("/suppliers/sla-alerts/recent").then(({ data }) => setAlerts(data.alerts || [])).catch(() => {});
  };

  useEffect(() => {
    API.get("/settings/sla-watch")
      .then(({ data }) => {
        setEnabled(!!data.enabled);
        setFloorMinutes(data.floor_minutes ?? 180);
        setPctIncrease(data.pct_increase ?? 100);
        setMinRecentN(data.min_recent_n ?? 3);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadAlerts();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/sla-watch", {
        enabled,
        floor_minutes: floorMinutes,
        pct_increase: pctIncrease,
        min_recent_n: minRecentN,
      });
      toast.success("SLA watch settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setTestingRun(true);
    try {
      const { data } = await API.post("/scheduler/sla-watch/run-now");
      toast.success(`Scan complete — ${data.alerts_emitted} new alerts emitted`);
      loadAlerts();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setTestingRun(false); }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} h`;
    return `${(seconds / 86400).toFixed(1)} d`;
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading...</div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="sla-watch-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Supplier Response SLA Watch</h3>
        <p className="text-xs text-muted-foreground">
          Proactively detect suppliers whose response times are getting slower. The system compares each
          supplier's average response time over the <strong>last 30 days</strong> against their baseline
          (<strong>30&ndash;90 days ago</strong>) and alerts admins when performance degrades significantly.
        </p>
      </div>

      <div className="flex items-center justify-between border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Enable SLA Alerts</p>
          <p className="text-[10px] text-muted-foreground">Scanner runs daily. Each supplier alerted at most once per 24h.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="sla-watch-enabled-toggle" />
      </div>

      {enabled && (
        <div className="space-y-4 border border-border rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-semibold">Response-time floor (minutes)</Label>
              <Input type="number" min={30} value={floorMinutes} onChange={e => setFloorMinutes(Number(e.target.value))} className="mt-1" data-testid="sla-floor-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Ignore blips below this ({formatDuration(floorMinutes * 60)})</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Increase threshold (%)</Label>
              <Input type="number" min={10} value={pctIncrease} onChange={e => setPctIncrease(Number(e.target.value))} className="mt-1" data-testid="sla-pct-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Alert when recent is at least this much slower than baseline</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">Min recent tasks</Label>
              <Input type="number" min={1} value={minRecentN} onChange={e => setMinRecentN(Number(e.target.value))} className="mt-1" data-testid="sla-min-n-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Require at least this many tasks to be sure</p>
            </div>
          </div>

          <div className="text-[11px] text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 rounded px-3 py-2">
            Example: with floor <strong>{floorMinutes} min</strong> and increase <strong>{pctIncrease}%</strong>, a supplier
            averaging <strong>30 min</strong> previously and <strong>{Math.round(30 * (1 + pctIncrease / 100) + 5)} min</strong> in the last 30 days
            would <strong>{((30 * (1 + pctIncrease / 100) + 5) >= floorMinutes) ? "trigger an alert" : "not trigger (below floor)"}</strong>.
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-sla-watch-btn">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
        {enabled && (
          <Button variant="outline" onClick={runNow} disabled={testingRun} data-testid="run-sla-watch-now-btn">
            {testingRun ? "Scanning..." : "Scan now"}
          </Button>
        )}
      </div>

      {/* Recent alerts */}
      <div className="border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold font-[Chivo]">Recent Alerts</h4>
          <Button size="sm" variant="ghost" onClick={loadAlerts} className="h-6" data-testid="refresh-sla-alerts-btn">
            <ArrowsClockwise size={12} />
          </Button>
        </div>
        {alerts.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">No alerts raised yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {alerts.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 text-xs" data-testid={`sla-alert-${a.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{a.company_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    Recent avg <strong>{formatDuration(a.recent_avg_seconds)}</strong> vs baseline <strong>{formatDuration(a.baseline_avg_seconds)}</strong> · {a.pct_worse}% slower
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground ml-2">{new Date(a.alerted_at).toLocaleDateString("en-GB")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function QuoteFollowupsSettingsSection() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);

  const fetchCfg = useCallback(async () => {
    try {
      const { data } = await API.get("/settings/quote-followups");
      setCfg(data);
    } catch { toast.error("Failed to load settings"); }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const { data } = await API.get("/quote-followups/recent?limit=25");
      setLogs(data.logs || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchCfg(); fetchLogs(); }, [fetchCfg, fetchLogs]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await API.put("/settings/quote-followups", cfg);
      toast.success("Follow-up settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await API.post("/scheduler/quote-followups/run-now");
      toast.success(`Scan complete — sent ${data.sent} chase email(s).`);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  if (!cfg) return <div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const updateIntervals = (csv) => {
    const arr = csv.split(",").map(s => parseInt(s.trim(), 10)).filter(n => Number.isFinite(n) && n > 0).slice(0, 5);
    setCfg({ ...cfg, intervals_days: arr });
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="quote-followups-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold font-[Chivo]">Automated Quote Follow-ups</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Gently chase clients who haven't responded to a quote. Stops automatically on approval, decline, or when max chases reached.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Enabled</span>
          <Switch checked={!!cfg.enabled} onCheckedChange={v => setCfg({ ...cfg, enabled: v })} data-testid="followups-enabled" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label className="text-xs">Chase intervals (days, comma-separated)</Label>
          <Input
            value={(cfg.intervals_days || []).join(", ")}
            onChange={e => updateIntervals(e.target.value)}
            placeholder="3, 7, 14"
            data-testid="followups-intervals"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            Days after the last send date. Example: <strong>3, 7, 14</strong> sends a chase 3 days after the quote is sent, then 7 days after that, then 14 days after that.
          </p>
        </div>
        <div>
          <Label className="text-xs">Max chases per quote</Label>
          <Input
            type="number"
            min={1} max={10}
            value={cfg.max_chases || 3}
            onChange={e => setCfg({ ...cfg, max_chases: parseInt(e.target.value, 10) || 3 })}
            data-testid="followups-max-chases"
          />
          <p className="text-[10px] text-muted-foreground mt-1">After this many chases, we stop automatically to avoid nagging.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <label className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
          <input type="checkbox" checked={!!cfg.stop_on_reply} onChange={e => setCfg({ ...cfg, stop_on_reply: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" data-testid="followups-stop-on-reply" />
          <div><p className="text-xs font-semibold">Stop on reply</p><p className="text-[10px] text-muted-foreground">Halt when status changes to approved / declined / converted.</p></div>
        </label>
        <label className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
          <input type="checkbox" checked={!!cfg.only_weekdays} onChange={e => setCfg({ ...cfg, only_weekdays: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" data-testid="followups-only-weekdays" />
          <div><p className="text-xs font-semibold">Weekdays only</p><p className="text-[10px] text-muted-foreground">Skip weekends for a more professional cadence.</p></div>
        </label>
        <label className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
          <input type="checkbox" checked={!!cfg.bcc_admin} onChange={e => setCfg({ ...cfg, bcc_admin: e.target.checked })} className="mt-0.5 h-4 w-4 accent-primary" data-testid="followups-bcc-admin" />
          <div><p className="text-xs font-semibold">BCC sender</p><p className="text-[10px] text-muted-foreground">Get a copy of every chase sent from your tenant.</p></div>
        </label>
      </div>

      <div>
        <Label className="text-xs">Subject template</Label>
        <Input
          value={cfg.default_subject || ""}
          onChange={e => setCfg({ ...cfg, default_subject: e.target.value })}
          data-testid="followups-subject"
        />
        <p className="text-[10px] text-muted-foreground mt-1">Placeholders: <code>{"{reference}"}</code>, <code>{"{title}"}</code>, <code>{"{client_name}"}</code>, <code>{"{company}"}</code>, <code>{"{total}"}</code>, <code>{"{validity_days}"}</code>, <code>{"{sender_name}"}</code>, <code>{"{sent_date}"}</code></p>
      </div>
      <div>
        <Label className="text-xs">Message template</Label>
        <Textarea
          rows={8}
          value={cfg.default_message || ""}
          onChange={e => setCfg({ ...cfg, default_message: e.target.value })}
          className="font-mono text-xs"
          data-testid="followups-message"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={saving} data-testid="followups-save">{saving ? "Saving…" : "Save settings"}</Button>
        <Button variant="outline" onClick={runNow} disabled={running} data-testid="followups-run-now">
          <Lightning size={14} className="mr-1" /> {running ? "Scanning…" : "Run scan now"}
        </Button>
      </div>

      <div className="border border-border rounded-lg p-4 bg-card" data-testid="followups-recent-log">
        <h3 className="text-sm font-semibold font-[Chivo] mb-2">Recent chases sent</h3>
        {logs.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3">No chase emails sent yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {logs.map(l => (
              <div key={l.id || `${l.quote_id}-${l.chase_number}-${l.sent_at}`} className="flex items-center justify-between py-2 text-xs" data-testid={`followups-log-${l.quote_id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{l.reference || l.quote_id} → {l.to_email}</p>
                  <p className="text-[10px] text-muted-foreground truncate">Chase #{l.chase_number} · {l.subject}</p>
                </div>
                <span className="text-[10px] text-muted-foreground ml-2 shrink-0">{new Date(l.sent_at).toLocaleDateString("en-GB")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function CashflowSnapshotSettingsSection() {
  const [enabled, setEnabled] = useState(true);
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, snapRes] = await Promise.all([
        API.get("/settings/cashflow-snapshot"),
        API.get("/reports/cashflow-snapshot"),
      ]);
      setEnabled(cfgRes.data.enabled !== false);
      setSnap(snapRes.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const fmt = (v) => `£${(Number(v) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put("/settings/cashflow-snapshot", { enabled });
      toast.success("Saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const { data } = await API.post("/scheduler/cashflow-snapshot/run-now");
      toast.success(`Sent to ${data.recipients} recipient${data.recipients !== 1 ? "s" : ""}`);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="cashflow-snapshot-settings">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Monthly Cashflow Snapshot</h3>
        <p className="text-xs text-muted-foreground">A pinned-on-the-1st-of-month email to all admins summarising last 30 days invoiced vs paid, outstanding balance, average days-to-payment, claims awaiting verification, and your top 5 outstanding clients.</p>
      </div>

      <div className="flex items-center justify-between border border-border rounded-lg p-4">
        <div>
          <p className="text-sm font-medium">Send the monthly cashflow email</p>
          <p className="text-[11px] text-muted-foreground">Runs automatically on the 1st of each month at 09:00 UTC. Goes to every admin and super-admin.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="cashflow-enabled-toggle" />
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-cashflow-btn">
          {saving ? "Saving…" : "Save Settings"}
        </Button>
        <Button onClick={handleSendNow} disabled={sending} variant="outline" data-testid="send-cashflow-now-btn">
          {sending ? "Sending…" : "Send now"}
        </Button>
      </div>

      {snap && (
        <div className="border border-border rounded-lg p-4 space-y-3" data-testid="cashflow-preview">
          <div className="flex items-baseline justify-between">
            <h4 className="text-sm font-semibold font-[Chivo]">Preview &middot; <span className="text-muted-foreground font-normal">{snap.period_label}</span></h4>
            <button onClick={loadAll} className="text-[11px] text-primary hover:underline" data-testid="cashflow-refresh">Refresh</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-muted/40 rounded p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Invoiced (30d)</p>
              <p className="text-lg font-bold font-[Chivo] tabular-nums mt-1">{fmt(snap.last_30_invoiced)}</p>
              <p className="text-[10px] text-muted-foreground">{snap.last_30_invoice_count} invoice{snap.last_30_invoice_count !== 1 ? "s" : ""}</p>
            </div>
            <div className="bg-emerald-50/60 dark:bg-emerald-900/20 rounded p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Paid (30d)</p>
              <p className="text-lg font-bold font-[Chivo] tabular-nums mt-1 text-emerald-700 dark:text-emerald-400">{fmt(snap.last_30_paid)}</p>
              <p className="text-[10px] text-muted-foreground">{snap.last_30_paid_count} invoice{snap.last_30_paid_count !== 1 ? "s" : ""}</p>
            </div>
            <div className="bg-rose-50/60 dark:bg-rose-900/20 rounded p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Outstanding</p>
              <p className="text-lg font-bold font-[Chivo] tabular-nums mt-1 text-rose-700 dark:text-rose-400">{fmt(snap.outstanding_total)}</p>
              <p className="text-[10px] text-muted-foreground">{snap.outstanding_count} invoice{snap.outstanding_count !== 1 ? "s" : ""}</p>
            </div>
            <div className="bg-muted/40 rounded p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Avg days to pay</p>
              <p className="text-lg font-bold font-[Chivo] tabular-nums mt-1">{snap.avg_days_to_payment != null ? `${snap.avg_days_to_payment} d` : "—"}</p>
              <p className="text-[10px] text-muted-foreground">{snap.avg_days_sample} sample{snap.avg_days_sample !== 1 ? "s" : ""}</p>
            </div>
            <div className="bg-amber-50/60 dark:bg-amber-900/20 rounded p-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Pending verify</p>
              <p className="text-lg font-bold font-[Chivo] tabular-nums mt-1 text-amber-700 dark:text-amber-300">{snap.pending_verification}</p>
              <p className="text-[10px] text-muted-foreground">claimed by clients</p>
            </div>
          </div>

          {snap.top_outstanding_clients && snap.top_outstanding_clients.length > 0 && (
            <div className="pt-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Top outstanding clients</p>
              <div className="border border-border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Client</th>
                      <th className="text-right px-3 py-2 font-medium">Invoices</th>
                      <th className="text-right px-3 py-2 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snap.top_outstanding_clients.map((c, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2">{c.client}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{c.count}</td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmt(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function PricingCalculatorSection() {
  const [mode, setMode] = useState("reverse"); // "reverse" or "forward"
  const [templates, setTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState("");
  const [markupAPct, setMarkupAPct] = useState(30);
  const [markupBPct, setMarkupBPct] = useState(10);
  const [markupALabel, setMarkupALabel] = useState("Our Mark Up");
  const [markupBLabel, setMarkupBLabel] = useState("Client Mark Up");
  const [vatEnabled, setVatEnabled] = useState(false);
  const [vatRate, setVatRate] = useState(20);
  const [forwardCost, setForwardCost] = useState(1000);
  const [reverseTotal, setReverseTotal] = useState(1000);

  useEffect(() => {
    API.get("/markup-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
    // Auto-load tenant VAT default
    API.get("/settings/vat").then(({ data }) => {
      if (data.vat_enabled) { setVatEnabled(true); setVatRate(data.vat_rate || 20); }
    }).catch(() => {});
  }, []);

  const handleLoadTemplate = (id) => {
    setSelectedTplId(id);
    const t = templates.find(t => t.id === id);
    if (!t) return;
    setMarkupAPct(t.markup_a_pct || 0);
    setMarkupBPct(t.markup_b_pct || 0);
    setMarkupALabel(t.markup_a_label || "Our Mark Up");
    setMarkupBLabel(t.markup_b_label || "Client Mark Up");
  };

  const aFactor = 1 + Number(markupAPct || 0) / 100;
  const bFactor = 1 + Number(markupBPct || 0) / 100;
  const vFactor = vatEnabled ? 1 + Number(vatRate || 0) / 100 : 1;

  // Forward maths (existing breakdown semantics: cost → A → B → +VAT)
  const fwdCost = Number(forwardCost || 0);
  const fwdAfterA = fwdCost * aFactor;
  const fwdAAmount = fwdAfterA - fwdCost;
  const fwdAfterB = fwdAfterA * bFactor;
  const fwdBAmount = fwdAfterB - fwdAfterA;
  const fwdVatAmount = fwdAfterB * (vFactor - 1);
  const fwdTotal = fwdAfterB + fwdVatAmount;
  const fwdProfit = fwdAAmount + fwdBAmount;

  // Reverse maths: take the inc-VAT total, peel back VAT, then markup B, then markup A
  const revTotal = Number(reverseTotal || 0);
  const revAfterB = vFactor > 0 ? revTotal / vFactor : revTotal;
  const revVatAmount = revTotal - revAfterB;
  const revAfterA = bFactor > 0 ? revAfterB / bFactor : revAfterB;
  const revBAmount = revAfterB - revAfterA;
  const revCost = aFactor > 0 ? revAfterA / aFactor : revAfterA;
  const revAAmount = revAfterA - revCost;
  const revProfit = revAAmount + revBAmount;

  const fmt = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="max-w-3xl space-y-5" data-testid="pricing-calculator">
      <div>
        <h3 className="text-base font-semibold font-[Chivo] mb-1">Pricing Calculator</h3>
        <p className="text-xs text-muted-foreground">Work out a client price from a supplier cost (forward), or back-solve a target client price into the supplier cost it implies (reverse). Useful for negotiating with suppliers when the client has a fixed budget.</p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex border border-border rounded-md overflow-hidden text-xs" data-testid="pricing-mode-toggle">
        <button
          onClick={() => setMode("forward")}
          className={`px-3 py-1.5 ${mode === "forward" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          data-testid="pricing-mode-forward"
        >Forward (cost → total)</button>
        <button
          onClick={() => setMode("reverse")}
          className={`px-3 py-1.5 border-l border-border ${mode === "reverse" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
          data-testid="pricing-mode-reverse"
        >Reverse (total → cost)</button>
      </div>

      {/* Inputs */}
      <div className="border border-border rounded-lg p-4 space-y-4">
        <div>
          <Label className="text-xs">Load from template (optional)</Label>
          <select
            value={selectedTplId}
            onChange={(e) => handleLoadTemplate(e.target.value)}
            className="w-full mt-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
            data-testid="pricing-template-select"
          >
            <option value="">— Select a markup template —</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.name} ({t.markup_a_pct}% + {t.markup_b_pct}%)</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{markupALabel} (A)</Label>
            <div className="relative mt-1">
              <Input
                type="number"
                step="0.5"
                value={markupAPct}
                onChange={e => setMarkupAPct(e.target.value)}
                className="pr-8"
                data-testid="pricing-markup-a"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">{markupBLabel} (B)</Label>
            <div className="relative mt-1">
              <Input
                type="number"
                step="0.5"
                value={markupBPct}
                onChange={e => setMarkupBPct(e.target.value)}
                className="pr-8"
                data-testid="pricing-markup-b"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} data-testid="pricing-vat-toggle" />
            <span>Include VAT</span>
          </label>
          {vatEnabled && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Rate</span>
              <div className="relative w-24">
                <Input
                  type="number"
                  step="0.5"
                  value={vatRate}
                  onChange={e => setVatRate(e.target.value)}
                  className="pr-7 h-8 text-sm"
                  data-testid="pricing-vat-rate"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
              </div>
            </div>
          )}
        </div>

        {/* Mode-specific input */}
        {mode === "forward" ? (
          <div>
            <Label className="text-xs">Supplier cost</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
              <Input
                type="number"
                step="0.01"
                value={forwardCost}
                onChange={e => setForwardCost(e.target.value)}
                className="pl-7 text-base font-[Chivo]"
                data-testid="pricing-forward-cost"
              />
            </div>
          </div>
        ) : (
          <div>
            <Label className="text-xs">Target client price {vatEnabled ? "(inc. VAT)" : "(ex. VAT)"}</Label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">£</span>
              <Input
                type="number"
                step="0.01"
                value={reverseTotal}
                onChange={e => setReverseTotal(e.target.value)}
                className="pl-7 text-base font-[Chivo]"
                data-testid="pricing-reverse-total"
              />
            </div>
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="border border-border rounded-lg p-4 space-y-2" data-testid="pricing-breakdown">
        <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">
          {mode === "reverse" ? "Reverse Breakdown" : "Forward Breakdown"}
        </h4>
        {mode === "forward" ? (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              <tr><td className="py-2 text-muted-foreground">Supplier cost</td><td className="py-2 text-right font-mono tabular-nums" data-testid="pricing-fwd-cost">{fmt(fwdCost)}</td></tr>
              <tr><td className="py-2 text-muted-foreground">+ {markupALabel} ({markupAPct}%)</td><td className="py-2 text-right font-mono tabular-nums text-primary" data-testid="pricing-fwd-a-amount">{fmt(fwdAAmount)}</td></tr>
              <tr><td className="py-2 text-muted-foreground italic text-xs">Subtotal after A</td><td className="py-2 text-right font-mono tabular-nums text-xs italic">{fmt(fwdAfterA)}</td></tr>
              <tr><td className="py-2 text-muted-foreground">+ {markupBLabel} ({markupBPct}%)</td><td className="py-2 text-right font-mono tabular-nums text-violet-600 dark:text-violet-400" data-testid="pricing-fwd-b-amount">{fmt(fwdBAmount)}</td></tr>
              <tr><td className="py-2 text-muted-foreground italic text-xs">Subtotal after B (ex VAT)</td><td className="py-2 text-right font-mono tabular-nums text-xs italic" data-testid="pricing-fwd-after-b">{fmt(fwdAfterB)}</td></tr>
              {vatEnabled && (
                <tr><td className="py-2 text-muted-foreground">+ VAT ({vatRate}%)</td><td className="py-2 text-right font-mono tabular-nums" data-testid="pricing-fwd-vat">{fmt(fwdVatAmount)}</td></tr>
              )}
              <tr className="border-t-2 border-border bg-muted/30">
                <td className="py-2.5 px-2 font-semibold">Client price {vatEnabled ? "(inc. VAT)" : ""}</td>
                <td className="py-2.5 px-2 text-right font-mono tabular-nums text-base font-bold font-[Chivo]" data-testid="pricing-fwd-total">{fmt(fwdTotal)}</td>
              </tr>
              <tr className="bg-emerald-50/50 dark:bg-emerald-900/10">
                <td className="py-2 px-2 text-emerald-700 dark:text-emerald-400 font-semibold">Your profit (A + B)</td>
                <td className="py-2 px-2 text-right font-mono tabular-nums font-semibold text-emerald-700 dark:text-emerald-400" data-testid="pricing-fwd-profit">{fmt(fwdProfit)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              <tr className="bg-muted/30">
                <td className="py-2.5 px-2 font-semibold">Target client price {vatEnabled ? "(inc. VAT)" : ""}</td>
                <td className="py-2.5 px-2 text-right font-mono tabular-nums text-base font-bold font-[Chivo]" data-testid="pricing-rev-total">{fmt(revTotal)}</td>
              </tr>
              {vatEnabled && (
                <>
                  <tr><td className="py-2 text-muted-foreground">− VAT ({vatRate}%)</td><td className="py-2 text-right font-mono tabular-nums" data-testid="pricing-rev-vat">{fmt(revVatAmount)}</td></tr>
                  <tr><td className="py-2 text-muted-foreground italic text-xs">Subtotal ex VAT</td><td className="py-2 text-right font-mono tabular-nums text-xs italic" data-testid="pricing-rev-ex-vat">{fmt(revAfterB)}</td></tr>
                </>
              )}
              <tr><td className="py-2 text-muted-foreground">− {markupBLabel} ({markupBPct}%)</td><td className="py-2 text-right font-mono tabular-nums text-violet-600 dark:text-violet-400" data-testid="pricing-rev-b-amount">{fmt(revBAmount)}</td></tr>
              <tr><td className="py-2 text-muted-foreground italic text-xs">Subtotal after stripping B</td><td className="py-2 text-right font-mono tabular-nums text-xs italic">{fmt(revAfterA)}</td></tr>
              <tr><td className="py-2 text-muted-foreground">− {markupALabel} ({markupAPct}%)</td><td className="py-2 text-right font-mono tabular-nums text-primary" data-testid="pricing-rev-a-amount">{fmt(revAAmount)}</td></tr>
              <tr className="border-t-2 border-border bg-emerald-50/40 dark:bg-emerald-900/20">
                <td className="py-2.5 px-2 font-semibold text-emerald-800 dark:text-emerald-300">Implied supplier cost (max budget)</td>
                <td className="py-2.5 px-2 text-right font-mono tabular-nums text-base font-bold font-[Chivo] text-emerald-800 dark:text-emerald-300" data-testid="pricing-rev-cost">{fmt(revCost)}</td>
              </tr>
              <tr>
                <td className="py-2 px-2 text-muted-foreground text-xs">Your profit at this price</td>
                <td className="py-2 px-2 text-right font-mono tabular-nums text-emerald-700 dark:text-emerald-400 text-xs font-semibold" data-testid="pricing-rev-profit">{fmt(revProfit)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {mode === "reverse" && (
        <p className="text-[11px] text-muted-foreground italic" data-testid="pricing-rev-helper">
          Tip: the "implied supplier cost" is the maximum you could pay your supplier and still hit your target client price — useful when negotiating with quotes.
        </p>
      )}
    </div>
  );
}


/** Phase 253 — Orphan-by-email wipe tool. Lets a super_admin/admin enter an
 * email address to find any record (user or vendor) tied to it across the
 * tenant database, then force-delete every match (plus pending invite/reset
 * tokens). Used when a previous account blocks recreating a fresh user. */
function OrphanWipeToolPanel({ onWiped }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleLookup = async () => {
    const e = (email || "").trim();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await API.post("/users/lookup-by-email", { email: e });
      setLookup(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };

  const handleWipe = async () => {
    if (!lookup || (lookup.user_count + lookup.vendor_count === 0)) return;
    const ok = window.confirm(
      `This will permanently delete:\n` +
      `  • ${lookup.user_count} user account(s)\n` +
      `  • ${lookup.vendor_count} supplier record(s)\n` +
      `…with email ${lookup.email}.\n\nProceed?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await API.post("/users/force-delete-by-email", { email: lookup.email, confirm: true });
      toast.success(`Wiped ${data.users_deleted} user(s), ${data.vendors_deleted} supplier(s), ${data.tokens_deleted} token(s)`);
      setLookup(null);
      setEmail("");
      onWiped?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Force-delete failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3 underline-offset-2 hover:underline"
        data-testid="orphan-wipe-toggle"
      >
        Can't recreate a user? Find &amp; wipe orphan by email →
      </button>
    );
  }
  return (
    <div className="border border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 rounded-md p-3 mb-3" data-testid="orphan-wipe-panel">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-300">Find &amp; wipe orphan by email</p>
          <p className="text-[10px] text-amber-800/80 dark:text-amber-400/80">
            Removes deactivated user accounts and supplier records that block creating a fresh account with the same email.
          </p>
        </div>
        <button onClick={() => { setOpen(false); setLookup(null); setEmail(""); }} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="orphan-wipe-close">close</button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setLookup(null); }}
          placeholder="email@example.com"
          className="h-8 text-xs flex-1 min-w-[180px]"
          data-testid="orphan-wipe-email"
        />
        <Button size="sm" variant="outline" onClick={handleLookup} disabled={busy || !email.trim()} data-testid="orphan-wipe-find-btn">
          {busy && !lookup ? "Searching…" : "Find"}
        </Button>
        {lookup && (lookup.user_count + lookup.vendor_count) > 0 && (
          <Button size="sm" variant="destructive" onClick={handleWipe} disabled={busy} data-testid="orphan-wipe-confirm-btn">
            {busy ? "Wiping…" : `Wipe ${lookup.user_count + lookup.vendor_count} record(s)`}
          </Button>
        )}
      </div>
      {lookup && (
        <div className="mt-2 text-[11px] space-y-1" data-testid="orphan-wipe-results">
          {lookup.user_count + lookup.vendor_count === 0 ? (
            <p className="text-muted-foreground">No records found for <code>{lookup.email}</code> — you can create the user now.</p>
          ) : (
            <>
              <p className="text-amber-900 dark:text-amber-300 font-medium">Found {lookup.user_count + lookup.vendor_count} record(s):</p>
              {lookup.users.map(u => (
                <p key={u._id} className="pl-2">• User: <span className="font-mono">{u.name || u._id}</span> [{u.role}] {u.active === false ? "· INACTIVE" : ""}</p>
              ))}
              {lookup.vendors.map(v => (
                <p key={v.id} className="pl-2">• Supplier: <span className="font-mono">{v.company_name || v.id}</span> [{v.status || "unknown"}]</p>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}




/** Phase 255 — Inline role reassignment dropdown.
 *
 * Renders the user's current role as an editable Select. Picking a new role
 * fires an immediate PUT /users/{id} with the new role; on success the table
 * refreshes (and the user "moves" between Admins / In-house / Suppliers
 * sub-tabs because those tabs filter purely on `role`).
 *
 * Note: switching role to "vendor" here just flags the user's auth account
 * as a supplier — it does NOT auto-create a `vendors` collection record.
 * Use the Suppliers page if you need a full supplier profile (compliance
 * docs, scorecard, etc.).
 */
const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin", group: "Admin" },
  { value: "admin", label: "Admin", group: "Admin" },
  { value: "project_manager", label: "Project Manager", group: "Admin" },
  { value: "hs_manager", label: "H&S Manager", group: "Admin" },
  { value: "hr_manager", label: "HR Manager", group: "Admin" },
  { value: "team_member", label: "Team Member (in-house)", group: "In-house" },
  { value: "in_house", label: "In-house Operative", group: "In-house" },
  { value: "vendor", label: "Supplier (external)", group: "Supplier" },
];
function RoleReassignSelect({ user, canEdit, onChanged, customRoles = [] }) {
  const isAdminBase = ["super_admin", "admin", "project_manager"].includes(user.role);
  const currentVal = user.custom_role_id && isAdminBase ? `custom:${user.custom_role_id}` : (user.role || "team_member");
  const [val, setVal] = useState(currentVal);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(currentVal); }, [currentVal]);

  // If the user's role isn't in our preset list, show it as-is to avoid
  // silently mutating the value.
  const optionsForUser = ROLE_OPTIONS.some(o => o.value === user.role)
    ? ROLE_OPTIONS
    : [{ value: user.role, label: user.role, group: "Custom" }, ...ROLE_OPTIONS];
  const permissionOptions = isAdminBase
    ? customRoles.map(r => ({ value: `custom:${r.id}`, label: `${r.is_system ? "👑 " : ""}${r.name}`, name: r.name }))
    : [];

  const onChange = async (next) => {
    if (next === val) return;
    // Permission role (custom role) selected — assign it, base role unchanged
    if (next.startsWith("custom:")) {
      const roleId = next.slice(7);
      const r = customRoles.find(x => x.id === roleId);
      const ok = window.confirm(
        `Assign the "${r?.name || "permission"}" role to ${user.name}?\n\n` +
        `Their base role stays "${(user.role || "").replace(/_/g, " ")}" — this controls which portal sections they can access.`
      );
      if (!ok) { setVal(val); return; }
      setSaving(true);
      try {
        await API.put("/permissions/assign", { user_id: user._id, role_id: roleId });
        setVal(next);
        toast.success(`"${r?.name}" assigned to ${user.name}`);
        onChanged?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to assign role");
        setVal(val);
      } finally { setSaving(false); }
      return;
    }
    // Base role selected
    const clearingCustom = !!user.custom_role_id && isAdminBase;
    const baseChanged = next !== user.role;
    if (!baseChanged && !clearingCustom) { setVal(val); return; }
    const wasSupplier = user.role === "vendor" || next === "vendor";
    const msg = baseChanged
      ? `Reassign ${user.name}'s role from "${user.role}" → "${next}"?` +
        (clearingCustom ? `\n\nTheir permission role will also be removed (full access).` : "") +
        (wasSupplier
          ? `\n\nNOTE: switching to/from supplier here only changes the auth account. It does NOT touch the Suppliers directory record. If you need a proper supplier profile, use the Suppliers page.`
          : "")
      : `Remove ${user.name}'s permission role and revert to full "${next.replace(/_/g, " ")}" access?`;
    if (!window.confirm(msg)) { setVal(val); return; }
    setSaving(true);
    try {
      if (clearingCustom) await API.put("/permissions/assign", { user_id: user._id, role_id: null });
      if (baseChanged) await API.put(`/users/${user._id}`, { role: next });
      setVal(next);
      toast.success(`Role updated to "${next.replace(/_/g, " ")}"`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update role");
      setVal(val);
      onChanged?.();
    } finally { setSaving(false); }
  };

  if (!canEdit) {
    const cr = user.custom_role_id && customRoles.find(r => r.id === user.custom_role_id);
    return <Badge variant="outline" className="text-[10px] capitalize">{cr ? cr.name : user.role?.replace(/_/g, " ")}</Badge>;
  }

  return (
    <Select value={val} onValueChange={onChange} disabled={saving}>
      <SelectTrigger className="h-7 text-[11px] w-[180px]" data-testid={`role-select-${user._id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {/* Group headers */}
        {["Admin", "In-house", "Supplier", "Custom"].map(g => {
          const opts = optionsForUser.filter(o => o.group === g);
          if (!opts.length) return null;
          return (
            <div key={g}>
              <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-wider font-bold text-muted-foreground">{g}</div>
              {opts.map(o => (
                <SelectItem key={o.value} value={o.value} data-testid={`role-option-${user._id}-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </div>
          );
        })}
        {permissionOptions.length > 0 && (
          <div>
            <div className="px-2 pt-1 pb-0.5 text-[9px] uppercase tracking-wider font-bold text-muted-foreground">Permission Roles</div>
            {permissionOptions.map(o => (
              <SelectItem key={o.value} value={o.value} data-testid={`role-option-${user._id}-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}



/** Phase 256 — User audit log panel.
 *
 * Pulls the most recent 20 user-related audit log entries (filtered by
 * entity_type=user) and renders them as a collapsible card below the
 * user table. Entries surfaced include: create_user, update_user,
 * deactivate_user, delete_user, force_delete_email, role changes.
 *
 * Uses the existing GET /api/audit-logs endpoint (admin/PM only).
 */
function UserAuditLogPanel() {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/audit-logs", { params: { entity_type: "user", limit: 20 } });
      setLogs(data?.logs || []);
      setLoaded(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  const ACTION_LABELS = {
    create_user: { text: "User created", color: "text-emerald-600 dark:text-emerald-400" },
    update_user: { text: "User updated", color: "text-blue-600 dark:text-blue-400" },
    deactivate_user: { text: "Deactivated", color: "text-amber-600 dark:text-amber-400" },
    activate_user: { text: "Re-activated", color: "text-emerald-600 dark:text-emerald-400" },
    delete_user: { text: "User deleted", color: "text-red-600 dark:text-red-400" },
    force_delete_email: { text: "Wiped by email", color: "text-red-700 dark:text-red-300" },
    invite_user: { text: "Invited", color: "text-blue-600 dark:text-blue-400" },
    user_login: { text: "Login", color: "text-muted-foreground" },
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div className="mt-6 border border-border rounded-lg bg-card" data-testid="user-audit-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40 transition-colors rounded-t-lg"
        data-testid="user-audit-toggle"
      >
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-muted-foreground" weight="duotone" />
          <span className="text-sm font-semibold font-[Chivo]">User audit log</span>
          {loaded && <span className="text-[10px] text-muted-foreground">last {logs.length}</span>}
        </div>
        <span className={`text-[10px] text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      {open && (
        <div className="border-t border-border" data-testid="user-audit-list">
          <div className="flex items-center justify-between px-4 py-2 bg-muted/20">
            <p className="text-[10px] text-muted-foreground">Recent user account events. Audit-logged automatically.</p>
            <button onClick={load} disabled={loading} className="text-[10px] text-primary hover:underline disabled:opacity-50" data-testid="user-audit-refresh">
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {loading && !logs.length ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              <div className="inline-block animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : logs.length === 0 ? (
            <p className="p-6 text-center text-xs text-muted-foreground" data-testid="user-audit-empty">
              No user events yet.
            </p>
          ) : (
            <ul className="divide-y divide-border max-h-[360px] overflow-auto">
              {logs.map((l, idx) => {
                const lbl = ACTION_LABELS[l.action] || { text: (l.action || "").replace(/_/g, " "), color: "text-muted-foreground" };
                return (
                  <li key={l.id || idx} className="px-4 py-2.5 text-xs flex items-start gap-3" data-testid={`user-audit-row-${idx}`}>
                    <span className={`shrink-0 font-bold ${lbl.color} text-[10px] uppercase tracking-wider w-24`}>
                      {lbl.text}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground break-words">
                        <span className="font-medium">{l.user_name || "System"}</span>
                        {l.details ? <span className="text-muted-foreground"> — {l.details}</span> : null}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {fmtTime(l.created_at)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

